import React from "react";
import parse from "html-react-parser";
import dayjs from "dayjs";
import TeethIcon from "../../../assets/editor-icons/tooth.svg";
import { PreferencesContext } from "../../../PreferenceContext";

// Common styles
const styles = {
  container: {
    display: "flex",
    marginTop: "4px",
    borderTop: "1px solid #c7c5c5",
  },
  cellContainer: {
    minWidth: "130px",
    maxWidth: "130px",
  },
  cell: {
    fontWeight: "bold",
    backgroundColor: "#dfdddd",
    padding: "4px 0 4px 8px",
    borderRadius: "0 0 0 10px",
  },
  table: {
    width: "100%",
    tableLayout: "fixed" as const,
    borderCollapse: "collapse" as const,
  },
  th: {
    border: "1px solid #aaa9a9",
    borderTop: "none",
    padding: "8px",
    fontWeight: "bold" as const,
    textAlign: "center" as const,
    whiteSpace: "nowrap" as const,
    backgroundColor: "#f1f1f1",
  },
  td: {
    border: "1px solid #aaa9a9",
    textAlign: "center" as const,
    verticalAlign: "middle" as const,
    wordWrap: "break-word" as const,
  },
  tdLeftAlign: {
    border: "1px solid #aaa9a9",
    textAlign: "left" as const,
    verticalAlign: "middle" as const,
    wordWrap: "break-word" as const,
  },
  row: {
    pageBreakInside: "avoid" as const,
  },
  label: {
    fontSize: "10px",
  },
  followupContainer: {
    padding: "2px 8px",
    border: "1px solid #aaa9a9",
    borderRadius: "3px",
    fontSize: "11px",
    fontWeight: "bold",
    display: "inline-flex",
    alignItems: "center",
  },
  tableStyle: {
    width: "100%",
    tableLayout: "fixed",
    border: "1px solid #cbd5e1",
    borderCollapse: "collapse",
    marginTop: "4px",
    marginBottom: "6px",
    fontSize: "10px",
  },
  cellStyle: {
    border: "1px solid #cbd5e1",
    padding: "5px 7px",
    whiteSpace: "pre-line",
    verticalAlign: "center",
  },
  headerStyle: {
    backgroundColor: "#f1f5f8",
    fontWeight: "normal",
  }
};

const stripHtmlText = (value?: string | null) =>
  value
    ? value
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/g, " ")
        .trim()
    : "";

const hasChiefComplaintContent = (opdContent: any) =>
  Boolean(
    opdContent?.chiefComplaints?.some(
      (item: any) =>
        stripHtmlText(item?.opdSymptomName) ||
        item?.opdDurationValue > 0 ||
        stripHtmlText(item?.opdDurationUnit) ||
        stripHtmlText(item?.opdSeverity) ||
        item?.opdFrequencyValue > 0 ||
        stripHtmlText(item?.opdFrequencyUnit) ||
        stripHtmlText(item?.opdNature) ||
        stripHtmlText(item?.opdTiming) ||
        stripHtmlText(item?.opdAgravatedByName) ||
        stripHtmlText(item?.opdRelievedByName) ||
        stripHtmlText(item?.opdDetailNote),
    ) ||
    opdContent?.chiefComplaintNotes?.some((item: any) =>
      stripHtmlText(item?.opdNote),
    ),
  );

const hasInvestigationContent = (opdContent: any) =>
  Boolean(
    opdContent?.investigations?.some(
      (item: any) =>
        stripHtmlText(item?.investigationName) ||
        stripHtmlText(item?.investigationDetailNote),
    ) ||
    opdContent?.investigationNotes?.some((item: any) =>
      stripHtmlText(item?.note),
    ),
  );

// Vital Parameter Component
const VitalParameter = ({ opdContent }: { opdContent: any }) => {
  const renderVital = (label: string, value: number, unit: string) => {
    if (!value) return null;
    return (
      <div>
        <span style={styles.label}>{label}</span>
        <div style={{ fontWeight: "bold", fontSize: "13px" }}>
          {value} {unit}
        </div>
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          padding: "5px",
          gap: "25px",
          rowGap: "0px",
          alignItems: "flex-start",
        }}
      >
        {renderVital(
          "WEIGHT",
          opdContent?.examinationVitalParameters?.weight,
          "kg",
        )}
        {renderVital(
          "HEIGHT/LENGTH",
          opdContent?.examinationVitalParameters?.height,
          "cm",
        )}
        {renderVital(
          "BMI",
          opdContent?.examinationVitalParameters?.bmi,
          "kg/m²",
        )}
        {renderVital("BSA", opdContent?.examinationVitalParameters?.bsa, "m²")}
        {renderVital(
          "PR",
          opdContent?.examinationVitalParameters?.pulseRate,
          "bpm",
        )}
        {renderVital(
          "RR",
          opdContent?.examinationVitalParameters?.respiratoryRate,
          "min",
        )}
        {renderVital(
          "SpO2",
          opdContent?.examinationVitalParameters?.spo2Amount,
          "%",
        )}
        {renderVital(
          "TEMP",
          opdContent?.examinationVitalParameters?.temperatureAmount,
          "°F",
        )}
        {opdContent?.examinationVitalParameters?.bpDisplayValue &&
          opdContent?.examinationVitalParameters?.bpDisplayValue !== "/" && (
            <div>
              <span style={styles.label}>BP</span>
              <div style={{ fontWeight: "bold", fontSize: "13px" }}>
                {opdContent?.examinationVitalParameters?.bpDisplayValue} mmHg
              </div>
            </div>
          )}
        {renderVital(
          "RBS",
          opdContent?.examinationVitalParameters?.rbs,
          "mg/dl",
        )}
        {renderVital(
          "HC",
          opdContent?.examinationVitalParameters?.headCircumference,
          "cm",
        )}
        {renderVital(
          "MH ",
          opdContent?.examinationVitalParameters?.motherHeight,
          "cm",
        )}
        {renderVital(
          "FH ",
          opdContent?.examinationVitalParameters?.fatherHeight,
          "cm",
        )}
      </div>
    </div>
  );
};

// Special Medical Condition
const SpecialMedicalCondition = ({ opdContent }: { opdContent: any }) => {
  return (
    <div style={styles.container}>
      <div style={{ fontSize: "11px" }}>
        <div
          style={{
            padding: "5px 5px 0 5px",
            display: "flex",
            flexWrap: "wrap",
            gap: "5px",
          }}
        >
          {opdContent?.medicalCondition?.map((item: any, index: number) => (
            <span key={index} className="print-special-medical-condition">
              {item?.medicalConditionName}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

// Chief Complaint Component
const ChiefComplaint = ({ opdContent }: { opdContent: any }) => {
  return (
    <div style={styles.container}>
      <div style={styles.cellContainer}>
        <div style={{ ...styles.cell, fontSize: "10px" }}>CHIEF COMPLAINTS</div>
      </div>
      <div style={{ fontSize: "11px" }}>
        <div style={{ padding: "5px" }}>
          {opdContent?.chiefComplaints?.map((item: any, index: number) => (
            <span key={index}>
              {index === 0 ? "" : ", "}
              <span style={{ fontWeight: 600 }}>{item?.opdSymptomName}</span>
              {item?.opdDurationValue > 0 &&
                ` For ${item?.opdDurationValue} ${item?.opdDurationUnit}`}
              {item?.opdSeverity && `, ${item?.opdSeverity}`}
              {(item?.opdFrequencyValue > 0 || item?.opdFrequencyUnit > 0) &&
                `, ${item?.opdFrequencyValue ?? " "} ${
                  item?.opdFrequencyUnit ?? " "
                }`}
              {item?.opdNature && `, ${item.opdNature}`}
              {item?.opdTiming && `, ${item?.opdTiming}`}
              {/* {item?.opdAgravatedByName && `, ${item?.opdAgravatedByName}`}
              {item?.opdRelievedByName && `, ${item?.opdRelievedByName}`} */}
              {item?.opdAgravatedByName && (
                <>
                  {", "}
                  <span className="bold-600">Agravated by:</span>{" "}
                  {item.opdAgravatedByName}
                </>
              )}

              {item?.opdRelievedByName && (
                <>
                  {", "}
                  <span className="bold-600">Relieved by:</span>{" "}
                  {item.opdRelievedByName}
                </>
              )}
              {item?.opdDetailNote &&
                `, ${item.opdDetailNote.replace(/<[^>]+>/g, "")}`}
            </span>
          ))}
          {/* <div>
            {parse(
              opdContent?.chiefComplaints[0]?.opdDetailNote
                ?.replace(/^<p>/, "")
                ?.replace(/<\/p>$/, "")
            )}
          </div> */}
        </div>
        <div style={{ paddingLeft: "5px" }}>
          <div>
            {/* {parse(
              opdContent?.chiefComplaints[0]?.opdNote
                ?.replace(/^<p>/, "")
                ?.replace(/<\/p>$/, "")
            )} */}
            {parse(
              opdContent?.chiefComplaintNotes[0]?.opdNote
                ?.replace(/^<p>/, "")
                ?.replace(/<\/p>$/, ""),
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const DentalHistory = ({ opdContent }: { opdContent: any }) => {
  const historyData = opdContent?.memberProcedureHistory || [];
  const historyDetails = opdContent?.memberProcedureHistoryDetail || [];
  const historyDetailBodyParts =
    opdContent?.memberProcedureHistoryDetailMappedBodyPart || [];
  const memberProcedures = opdContent?.memberProcedure || [];

  const stripHtml = (html: string) =>
    html ? html.replace(/<[^>]*>/g, "").trim() : "";

  const getMemberProcedureMeta = (memberProcedureId: any) => {
    const memberProcedure = memberProcedures.find(
      (mp: any) => mp.memberProcedureId === memberProcedureId,
    );

    return {
      isGeneral: !!memberProcedure?.isGeneral,
      generalProcedureName: stripHtml(
        memberProcedure?.generalProcedureName || "",
      ),
    };
  };

  const detailEntries =
    historyDetails.length > 0
      ? historyDetails.map((item: any) => {
          //   const bps = historyDetailBodyParts
          //     .filter(
          //       (bp: any) =>
          //         bp.memberProcedureHistoryId === item.memberProcedureHistoryId ||
          //         bp.memberProcedureId === item.memberProcedureId,
          //     )
          //     .map((bp: any) =>
          //       [bp.bodyPartLabel, bp.bodyPartName].filter(Boolean).join(" "),
          //     )
          //     .filter(Boolean);
          //   return {
          //     name: item?.refHistoryDentalHistoryName,
          //     note: stripHtml(item?.note),
          //     bodyParts: bps,
          //     memberProcedureId: item?.memberProcedureId,
          //   };
          // })
          const bps = historyDetailBodyParts
            .filter(
              (bp: any) =>
                bp.memberProcedureHistoryDetailId ===
                item.memberProcedureHistoryDetailId,
            )
            .map((bp: any) =>
              [bp.bodyPartLabel, bp.bodyPartName].filter(Boolean).join(" "),
            )
            .filter(Boolean);
          return {
            name: item?.refHistoryDentalHistoryName,
            note: stripHtml(item?.note),
            bodyParts: bps,
            memberProcedureId: item?.memberProcedureId,
          };
        })
      : [];

  const groupedHistory = detailEntries.length
    ? detailEntries
    : Object?.values(
        historyData?.reduce((acc: any, item: any) => {
          const key = item?.refHistoryDentalHistoryName;

          if (!acc[key]) {
            acc[key] = {
              name: item?.refHistoryDentalHistoryName,
              note: stripHtml(item?.procedureHistoryNote),
              bodyParts: [],
              memberProcedureId: item?.memberProcedureId,
            };
          }

          if (item.bodyPartName) {
            acc[key].bodyParts.push(item?.bodyPartName);
          }

          return acc;
        }, {}),
      );

  // Main Notes
  const mainNotes = [
    ...new Set(
      historyData.map((x: any) => stripHtml(x?.note)).filter((x: string) => x),
    ),
  ];

  const hasHistoryContent =
    groupedHistory.some((item: any) => {
      const memberProcedureMeta = getMemberProcedureMeta(
        item?.memberProcedureId,
      );
      const partsText = (item?.bodyParts || []).filter(Boolean).join(", ");
      return (
        item?.name ||
        partsText ||
        (memberProcedureMeta.isGeneral &&
          memberProcedureMeta.generalProcedureName) ||
        item?.note
      );
    }) || mainNotes.length > 0;

  // Hide HISTORY section if no data
  if (!hasHistoryContent) {
    return null;
  }

  return (
    <div style={styles.container}>
      <div style={styles.cellContainer}>
        <div style={{ ...styles.cell, fontSize: "10px" }}>HISTORY</div>
      </div>
      <div
        style={{
          fontSize: "11px",
          textAlign: "left",
          lineHeight: "1.6",
          wordBreak: "break-word",
        }}
      >
        <div style={{ padding: "5px" }}>
          {groupedHistory?.map((item: any, index: number) => {
            const memberProcedureMeta = getMemberProcedureMeta(
              item?.memberProcedureId,
            );
            const partsText = (item?.bodyParts || [])
              .filter(Boolean)
              .join(", ");
            return (
              <span key={index} style={{ display: "inline" }}>
                <span style={{ fontWeight: 700 }}>{item?.name}</span>
                {partsText ? (
                  <>
                    {" - "}
                    <img
                      src={TeethIcon}
                      alt="tooth"
                      style={{
                        width: "12px",
                        height: "12px",
                        verticalAlign: "middle",
                      }}
                    />{" "}
                    {partsText}
                  </>
                ) : memberProcedureMeta.isGeneral &&
                  memberProcedureMeta.generalProcedureName ? (
                  <>
                    {" - "}
                    {memberProcedureMeta.generalProcedureName}
                    {/* {"(General procedure)"} */}
                  </>
                ) : null}
                {item?.note && `, ${item?.note}`}
                {index < groupedHistory.length - 1 && ", "}
              </span>
            );
          })}
        </div>
        {mainNotes?.length > 0 && (
          <div style={{ paddingLeft: "5px" }}>{mainNotes?.join(", ")}</div>
        )}
      </div>
    </div>
  );
};

const DentalExamination = ({ opdContent }: { opdContent: any }) => {
  const examinationData = opdContent?.memberProcedureExamination || [];
  const examDetails = opdContent?.memberProcedureExaminationDetail || [];
  const examDetailBodyParts =
    opdContent?.memberProcedureExaminationDetailMappedBodyPart || [];
  const memberProcedures = opdContent?.memberProcedure || [];

  const stripHtml = (html: string) =>
    html ? html.replace(/<[^>]*>/g, "").trim() : "";

  const getMemberProcedureMeta = (memberProcedureId: any) => {
    const memberProcedure = memberProcedures.find(
      (mp: any) => mp.memberProcedureId === memberProcedureId,
    );

    return {
      isGeneral: !!memberProcedure?.isGeneral,
      generalProcedureName: stripHtml(
        memberProcedure?.generalProcedureName || "",
      ),
    };
  };

  const detailEntries =
    examDetails.length > 0
      ? examDetails.map((item: any) => {
          //   const bps = examDetailBodyParts
          //     .filter(
          //       (bp: any) =>
          //         bp.memberProcedureExaminationId ===
          //           item.memberProcedureExaminationId ||
          //         bp.memberProcedureId === item.memberProcedureId,
          //     )
          //     .map((bp: any) =>
          //       [bp.bodyPartLabel, bp.bodyPartName].filter(Boolean).join(" "),
          //     )
          //     .filter(Boolean);
          //   return {
          //     name: item?.refMemberProcedureExaminationName,
          //     note: stripHtml(item?.note),
          //     bodyParts: bps,
          //     memberProcedureId: item?.memberProcedureId,
          //   };
          // })
          const bps = examDetailBodyParts
            .filter(
              (bp: any) =>
                bp.memberProcedureExaminationDetailId ===
                item.memberProcedureExaminationDetailId,
            )
            .map((bp: any) =>
              [bp.bodyPartLabel, bp.bodyPartName].filter(Boolean).join(" "),
            )
            .filter(Boolean);
          return {
            name: item?.refMemberProcedureExaminationName,
            note: stripHtml(item?.note),
            bodyParts: bps,
            memberProcedureId: item?.memberProcedureId,
          };
        })
      : [];

  const groupedExam =
    detailEntries.length > 0
      ? detailEntries
      : Object.values(
          examinationData?.reduce((acc: any, item: any) => {
            const key = item?.refMemberProcedureExaminationName;

            if (!acc[key]) {
              acc[key] = {
                name: item?.refMemberProcedureExaminationName,
                note: stripHtml(item?.procedureExaminationNote),
                bodyParts: [],
                memberProcedureId: item?.memberProcedureId,
              };
            }

            if (item?.bodyPartName) {
              acc[key].bodyParts.push(item?.bodyPartName);
            }

            return acc;
          }, {}),
        );

  // Main Notes
  const mainNotes = [
    ...new Set(
      examinationData
        .map((x: any) => stripHtml(x?.note))
        .filter((x: string) => x),
    ),
  ];

  const hasExaminationContent =
    groupedExam.some((item: any) => {
      const memberProcedureMeta = getMemberProcedureMeta(
        item?.memberProcedureId,
      );
      const partsText = (item?.bodyParts || []).filter(Boolean).join(", ");
      return (
        item?.name ||
        partsText ||
        (memberProcedureMeta.isGeneral &&
          memberProcedureMeta.generalProcedureName) ||
        item?.note
      );
    }) || mainNotes.length > 0;

  // Hide EXAMINATION section if no data
  if (!hasExaminationContent) {
    return null;
  }
  // if (groupedExam.length === 0 && mainNotes.length === 0) {
  //   return null;
  // }

  return (
    <div style={styles.container}>
      <div style={styles.cellContainer}>
        <div style={{ ...styles.cell, fontSize: "10px" }}>EXAMINATION</div>
      </div>

      <div
        style={{
          fontSize: "11px",
          textAlign: "left",
          lineHeight: "1.6",
          wordBreak: "break-word",
        }}
      >
        <div style={{ padding: "5px" }}>
          {groupedExam?.map((item: any, index: number) => {
            const memberProcedureMeta = getMemberProcedureMeta(
              item?.memberProcedureId,
            );
            const partsText = (item?.bodyParts || [])
              .filter(Boolean)
              .join(", ");
            return (
              <span key={index} style={{ display: "inline" }}>
                <span style={{ fontWeight: 700 }}>{item?.name}</span>
                {partsText ? (
                  <>
                    {" - "}
                    <img
                      src={TeethIcon}
                      alt="tooth"
                      style={{
                        width: "12px",
                        height: "12px",
                        verticalAlign: "middle",
                      }}
                    />{" "}
                    {partsText}
                  </>
                ) : memberProcedureMeta.isGeneral &&
                  memberProcedureMeta.generalProcedureName ? (
                  <>
                    {" - "}
                    {memberProcedureMeta.generalProcedureName}
                    {/* {"(General procedure)"} */}
                  </>
                ) : null}
                {item?.note && `, ${item?.note}`}
                {index < groupedExam.length - 1 && ", "}
              </span>
            );
          })}
        </div>

        {mainNotes?.length > 0 && (
          <div style={{ paddingLeft: "5px" }}>{mainNotes?.join(", ")}</div>
        )}
      </div>
    </div>
  );
};
// History Component

const History = ({ opdContent }: { opdContent: any }) => {
  const historySections = [
    {
      heading: "Past History",
      coll: opdContent?.patientHistoryPastHistory || [],
    },
    {
      heading: "Family History",
      coll: opdContent?.patientHistoryFamilyHistory || [],
    },
    {
      heading: "Personal History",
      coll: opdContent?.patientHistoryPersonalHistory || [],
    },
    {
      heading: "Diet History",
      coll: opdContent?.patientHistoryDietHistory || [],
    },
    {
      heading: "Perinatal History",
      coll: opdContent?.patientHistoryPerinatalHistory || [],
    },
    {
      heading: "Development History",
      coll: opdContent?.patientHistoryDevelopmentHistory || [],
    },
    {
      heading: "History Summary",
      coll: opdContent?.patientHistorySummary || [],
    },
    // { heading: 'Other History', coll: opdContent?.patientHistory || [] },
  ];

  const sanitizeNotes = (section: any[], heading: string) => {
    if (heading === "Perinatal History") {
      const merged = section
        .filter((item) =>
          [
            "Maturity of Baby",
            "Mode of Delivery",
            "Birth Weight of Baby",
            "Need for NICU Admission",
            "Perinatal Asphyxia Requiring Resuscitation",
          ].includes(item.heading),
        )
        .map((item) => item.note?.replace(/<\/?(div|p|br)[^>]*>/gi, "")?.trim())
        .filter(Boolean)
        .join(" / ");

      const others = section
        .filter(
          (item) =>
            ![
              "Maturity of Baby",
              "Mode of Delivery",
              "Birth Weight of Baby",
              "Need for NICU Admission",
              "Perinatal Asphyxia Requiring Resuscitation",
            ].includes(item.heading),
        )
        .map((item) => {
          const clean = item.note
            ?.replace(/<\/?(div|p|br)[^>]*>/gi, "")
            ?.trim();
          if (!clean) return null;
          if (item.heading === "Other Notes") return `<span>${clean}</span>`;
          return `<span><b>${item.heading}:</b> ${clean}</span>`;
        })
        .filter(Boolean);

      if (merged) {
        others.push(`<span><b>${merged}</b></span>`);
      }

      return others;
    }

    return section
      .map((item: any) => {
        const heading = item?.heading?.trim();
        // const cleanNote = item?.note
        const rawNote = item?.note
          ?.replace(/<\/?(div|p|br)[^>]*>/gi, "")
          ?.replace(/\n|\r/g, "")
          ?.replace(/\s+/g, " ")
          ?.trim();

        if (!rawNote) return null;
        if (heading === "Other Notes" || heading === "Summary") {
          return `<span>${rawNote}</span>`;
        }

        return `<span><b>${heading}:</b> ${rawNote}</span>`;

        // return cleanNote ? `<span>
        //   <span>
        //     <b>${item?.heading}:</b>
        //   </span>
        //   <span>
        //     ${item?.note
        //     ?.replace(/<\/?(div|p|br)[^>]*>/gi, '')
        //     ?.replace(/\n|\r/g, '')
        //     ?.replace(/\s+/g, ' ')
        //     ?.trim()}</span>
        // </span>` : null;
      })
      .filter(Boolean);
  };
  const hasHistoryData = historySections.some(
    (section) => sanitizeNotes(section.coll, section?.heading).length > 0,
  );
  if (!hasHistoryData) return null;

  return (
    <div style={styles.container}>
      <div style={styles.cellContainer}>
        <div style={{ ...styles.cell, fontSize: "10px" }}>HISTORY</div>
      </div>

      <div style={{ padding: "5px", fontSize: "11px" }} className="content">
        {historySections.map((section, index) => {
          const cleanedNotes = sanitizeNotes(section.coll, section?.heading);
          if (!cleanedNotes.length) return null;

          const lastIsMerged =
            section.heading === "Perinatal History" && cleanedNotes.length > 1
              ? cleanedNotes[cleanedNotes.length - 1]?.includes("<b>")
              : false;

          return (
            <div key={index}>
              <span
                style={{
                  textDecoration: "underline",
                  marginBottom: "2px",
                  marginRight:
                    section.heading !== "History Summary" ? "10px" : "0",
                }}
                className="bold-600"
              >
                {/* {section?.heading?.toUpperCase()} */}
                {section.heading !== "History Summary" &&
                  section.heading.toUpperCase()}
              </span>
              {/* {parse(cleanedNotes.join("; ") + ";")} */}
              <span>
                {parse(
                  cleanedNotes
                    // .map(note => (note ? note.replace(/;+\s*<\/span>$/, "</span>") : "")) // Safely handle nulls
                    .map((note, i) => {
                      const isLast = i === cleanedNotes.length - 1;
                      return isLast && lastIsMerged ? `<br/>${note}` : note;
                    })
                    .join("; ")
                    .replace(/;;+/g, ";") + ";",
                )}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Ophthalmology
const isOphthalSpeciality = (opdContent: any) =>
  [
    opdContent?.opdDetails?.specialityName,
    opdContent?.opdDetails?.departmentName,
    opdContent?.specialityName,
    opdContent?.departmentName,
  ].some((name) => /ophthal/i.test(String(name ?? "")));

const valueTextForExamination = (value: any) =>
  value !== null && value !== undefined && String(value).trim() !== "";

const getActiveRecords = (records: any[]) =>
  Array.isArray(records) ? records.filter((record) => !record?.isDeleted) : [];

const hasOphthalExaminationData = (opdContent: any) =>
  [
    getActiveRecords(opdContent?.visualAcuityExamination).some((item: any) =>
      [
        item?.odUnaidedDistance,
        item?.osUnaidedDistance,
        item?.odPinholeVa,
        item?.osPinholeVa,
        item?.odBestCorrectedVa,
        item?.osBestCorrectedVa,
        item?.odNearVision,
        item?.osNearVision,
        item?.odVisionWithGlass,
        item?.osVisionWithGlass,
        item?.odCurrentGlassesPrescription,
        item?.osCurrentGlassesPrescription,
      ].some(valueTextForExamination),
    ),
    getActiveRecords(opdContent?.visualAcuityExaminationDetail).some((item: any) =>
      valueTextForExamination(item?.dataVisualAcuity),
    ),
    getActiveRecords(opdContent?.refractionExamination).some((item: any) =>
      Object.keys(item ?? {}).some(
        (key) =>
          !key.endsWith("Id") &&
          /^(od|os)(Sphere|Cylinder|Axis|Vision|Glass)/i.test(key) &&
          valueTextForExamination(item[key]),
      ),
    ),
    getActiveRecords(opdContent?.slitLampExaminationDetail).some((item: any) =>
      valueTextForExamination(item?.dataSlitLamp),
    ),
    getActiveRecords(opdContent?.iopExamination).some((item: any) =>
      [
        item?.odIop,
        item?.osIop,
        item?.odMeasurementMethod,
        item?.osMeasurementMethod,
      ].some(valueTextForExamination),
    ),
    getActiveRecords(
      opdContent?.occularMotilityAndPupilsExaminationDetail,
    ).some((item: any) =>
      valueTextForExamination(item?.dataOccularMotilityAndPupils),
    ),
    getActiveRecords(
      opdContent?.posteriorSegmentFundusExaminationDetail,
    ).some((item: any) =>
      valueTextForExamination(item?.dataPosteriorSegmentFundus),
    ),
  ].some(Boolean);

const OphthalExaminationTables = ({ opdContent }: { opdContent: any }) => {
  if (
    !isOphthalSpeciality(opdContent) &&
    !hasOphthalExaminationData(opdContent)
  ) {
    return null;
  }
  const hasValue = valueTextForExamination;
  const valueText = (value: any) => (hasValue(value) ? String(value).trim() : "");
  const visibleRows = (rows: { label: string; od: string; os: string }[]) =>
    rows.filter((row) => row.od || row.os);
  const renderEyeTable = (title: string, firstColumn: string, rows: any[]) =>
    rows.length > 0 ? (
      <div key={title}>
        <div className="bold-600">{title}</div>
        <table style={styles.tableStyle}>
          <thead>
            <tr>
              <th style={{ ...styles.headerStyle, ...styles.cellStyle, textAlign: "left" }}>{firstColumn}</th>
              <th style={{ ...styles.headerStyle, ...styles.cellStyle, textAlign: "center" }}>Right Eye (OD)</th>
              <th style={{ ...styles.headerStyle, ...styles.cellStyle, textAlign: "center" }}>Left Eye (OS)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label}>
                <td style={{ ...styles.cellStyle, fontWeight: 600 }}>{row.label}</td>
                <td style={{ ...styles.cellStyle, textAlign: "center", fontWeight: 600 }}>
                  {row.od || "—"}
                </td>
                <td style={{ ...styles.cellStyle, textAlign: "center", fontWeight: 600 }}>
                  {row.os || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : null;

  // Visual Acuity
  const visualAcuity = getActiveRecords(opdContent?.visualAcuityExamination)[0];
  const visualAcuityDetails = getActiveRecords(
    opdContent?.visualAcuityExaminationDetail,
  );
  const visualAcuityFields: Record<string, [string, string]> = {
    "Unaided distance VA": ["odUnaidedDistance", "osUnaidedDistance"],
    "Pinhole VA": ["odPinholeVa", "osPinholeVa"],
    "Best corrected VA (BCVA)": ["odBestCorrectedVa", "osBestCorrectedVa"],
    "Near vision": ["odNearVision", "osNearVision"],
    "Vision with glass": ["odVisionWithGlass", "osVisionWithGlass"],
    "Current glasses prescription": [
      "odCurrentGlassesPrescription",
      "osCurrentGlassesPrescription",
    ],
  };
  const visualAcuityRows = visibleRows([
    ...Object.entries(visualAcuityFields)
      .filter(
        ([label]) =>
          label !== "Vision with glass" &&
          label !== "Current glasses prescription",
      )
      .map(([label, [odField, osField]]) => ({
        label,
        od: valueText(visualAcuity?.[odField]),
        os: valueText(visualAcuity?.[osField]),
      })),
    {
      label: "Final visual acuity",
      od: visualAcuityDetails
        .filter(
          (detail: any) =>
            !detail?.isDeleted &&
            String(detail?.forEye).toUpperCase() === "OD",
        )
        .map((detail: any) => valueText(detail?.dataVisualAcuity))
        .filter(Boolean)
        .join("\n"),
      os: visualAcuityDetails
        .filter(
          (detail: any) =>
            !detail?.isDeleted &&
            String(detail?.forEye).toUpperCase() === "OS",
        )
        .map((detail: any) => valueText(detail?.dataVisualAcuity))
        .filter(Boolean)
        .join("\n"),
    },
    ...Object.entries(visualAcuityFields)
      .filter(([label]) => label === "Vision with glass")
      .map(([label, [odField, osField]]) => ({
        label,
        od: valueText(visualAcuity?.[odField]),
        os: valueText(visualAcuity?.[osField]),
      })),
    {
      label: "Current glasses prescription",
      od: valueText(visualAcuity?.odCurrentGlassesPrescription),
      os: valueText(visualAcuity?.osCurrentGlassesPrescription),
    },
  ]);

  // Refraction
  const refraction = getActiveRecords(opdContent?.refractionExamination)[0];
  const refractionFields = {
    OD: {
      distant: [
        valueText(refraction?.odSphereDistant),
        valueText(refraction?.odCylinder),
        valueText(refraction?.odAxis),
        valueText(refraction?.odVisionDistant),
      ],
      near: [
        valueText(refraction?.odSphereNearAdd),
        valueText(refraction?.odVisionNearAdd),
      ],
      glass: valueText(refraction?.odGlassToBePrescribed),
    },
    OS: {
      distant: [
        valueText(refraction?.osSphereDistant),
        valueText(refraction?.osCylinder),
        valueText(refraction?.osAxis),
        valueText(refraction?.osVisionDistant),
      ],
      near: [
        valueText(refraction?.osSphereNearAdd),
        valueText(refraction?.osVisionNearAdd),
      ],
      glass: valueText(refraction?.osGlassToBePrescribed),
    },
  };
  const hasRefractionData = (["OD", "OS"] as const).some(
    (eye) =>
      [...refractionFields[eye].distant, ...refractionFields[eye].near].some(
        Boolean,
      ) || Boolean(refractionFields[eye].glass),
  );
  const refractionCell = (
    value: string,
    bold = false,
    rowSpan = 1,
  ) => (
    <td
      rowSpan={rowSpan}
      style={{
        ...styles.cellStyle,
        textAlign: "center",
        verticalAlign: "middle",
        fontWeight: bold ? 600 : "normal",
      }}
    >
      {value || "—"}
    </td>
  );
  const refractionTable = hasRefractionData ? (
    <div key="Refraction">
      <div className="bold-600">Refraction</div>
      <table style={styles.tableStyle}>
        <colgroup>
          <col style={{ width: "16.3%" }} />
          {Array.from({ length: 8 }, (_, index) => (
            <col key={index} style={{ width: "10.4625%" }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th rowSpan={2} style={{ ...styles.headerStyle, ...styles.cellStyle, textAlign: "center" }}>
              Parameter
            </th>
            <th colSpan={4} style={{ ...styles.headerStyle, ...styles.cellStyle, textAlign: "center" }}>
              Right Eye (OD)
            </th>
            <th colSpan={4} style={{ ...styles.headerStyle, ...styles.cellStyle, textAlign: "center" }}>
              Left Eye (OS)
            </th>
          </tr>
          <tr>
            {[
              "Sphere (SPH)",
              "Cylinder (CYL)",
              "Axis",
              "Vision (Vn)",
              "Sphere (SPH)",
              "Cylinder (CYL)",
              "Axis",
              "Vision (Vn)",
            ].map((label, index) => (
              <th
                key={`${label}-${index}`}
                style={{ ...styles.headerStyle, ...styles.cellStyle, textAlign: "center" }}
              >
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td
              style={{
                ...styles.cellStyle,
                fontWeight: 600,
              }}
            >
              DISTANT
            </td>
            {refractionCell(refractionFields.OD.distant[0], true)}
            {refractionCell(refractionFields.OD.distant[1], true, 2)}
            {refractionCell(refractionFields.OD.distant[2], true, 2)}
            {refractionCell(refractionFields.OD.distant[3], true)}
            {refractionCell(refractionFields.OS.distant[0], true)}
            {refractionCell(refractionFields.OS.distant[1], true, 2)}
            {refractionCell(refractionFields.OS.distant[2], true, 2)}
            {refractionCell(refractionFields.OS.distant[3], true)}
          </tr>
          <tr>
            <td style={{ ...styles.cellStyle, fontWeight: 600 }}>Near (ADD)</td>
            {refractionCell(refractionFields.OD.near[0], true)}
            {refractionCell(refractionFields.OD.near[1], true)}
            {refractionCell(refractionFields.OS.near[0], true)}
            {refractionCell(refractionFields.OS.near[1], true)}
          </tr>
          <tr>
            <td style={{ ...styles.cellStyle, fontWeight: 600 }}>
              Glass to be prescribed
            </td>
            <td
              colSpan={4}
              style={{ ...styles.cellStyle, textAlign: "center", fontWeight: 600 }}
            >
              {refractionFields.OD.glass || "—"}
            </td>
            <td
              colSpan={4}
              style={{ ...styles.cellStyle, textAlign: "center", fontWeight: 600 }}
            >
              {refractionFields.OS.glass || "—"}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  ) : null;

  // Slit Lamp
  const slitLampRows = [
    "Eyelids / Lashes",
    "Conjunctiva",
    "Cornea",
    "Anterior chamber",
    "Iris / Pupil",
    "Lens",
  ].map((structure) => {
    const matchingDetails = getActiveRecords(
      opdContent?.slitLampExaminationDetail,
    ).filter(
      (detail: any) =>
        String(detail?.forStructure ?? "").toLowerCase() ===
        structure.toLowerCase(),
    );
    return {
      label: structure,
      od: matchingDetails
        .filter((detail: any) => String(detail?.forEye).toUpperCase() === "OD")
        .map((detail: any) => valueText(detail?.dataSlitLamp))
        .filter(Boolean)
        .join("\n"),
      os: matchingDetails
        .filter((detail: any) => String(detail?.forEye).toUpperCase() === "OS")
        .map((detail: any) => valueText(detail?.dataSlitLamp))
        .filter(Boolean)
        .join("\n"),
    };
  });

  // IOP
  const iop = getActiveRecords(opdContent?.iopExamination)[0];
  const iopValueText = (value: any) => {
    if (!hasValue(value)) return "";
    const text = String(value).trim();
    return Number(text) === 0 && /^[0-9.\s]+$/.test(text) ? "" : text;
  };
  const iopRows = visibleRows([
    {
      label: "IOP (mmHg)",
      od: iopValueText(iop?.odIop),
      os: iopValueText(iop?.osIop),
    },
    {
      label: "Measurement method",
      od: valueText(iop?.odMeasurementMethod),
      os: valueText(iop?.osMeasurementMethod),
    },
  ]);

  // Occular Motility and Pupils
  const ocularDetails = getActiveRecords(
    opdContent?.occularMotilityAndPupilsExaminationDetail,
  );
  const ocularParameters = [
    "Extraocular Movements",
    "Pupillary Reaction",
    "Relative afferent pupillary defect",
    "Visual fields by confrontation",
  ];
  const ocularRows = visibleRows(
    ocularParameters.map((parameter) => {
      const matchingDetails = ocularDetails.filter(
        (detail: any) =>
          String(detail?.forParameter ?? "").toLowerCase() ===
          parameter.toLowerCase(),
      );
      return {
        label: parameter,
        od: matchingDetails
          .filter((detail: any) => String(detail?.forEye).toUpperCase() === "OD")
          .map((detail: any) => valueText(detail?.dataOccularMotilityAndPupils))
          .filter(Boolean)
          .join("\n"),
        os: matchingDetails
          .filter((detail: any) => String(detail?.forEye).toUpperCase() === "OS")
          .map((detail: any) => valueText(detail?.dataOccularMotilityAndPupils))
          .filter(Boolean)
          .join("\n"),
      };
    }),
  );

  // Posterior Segment/ Fundus
  const posteriorStructures: Record<string, string> = {
    VITREOUS: "Vitreous",
    "OPTIC DISC": "Optic disc",
    OPTIC_DISC: "Optic disc",
    CUP_TO_DISC_RATIO: "Cup-to-disc ratio (C/D)",
    "CUP-TO-DISC RATIO (C/D)": "Cup-to-disc ratio (C/D)",
    MACULA: "Macula",
    RETINAL_VESSELS: "Retinal vessels",
    PERIPHERAL_RETINA: "Peripheral retina",
  };
  const posteriorDetails = getActiveRecords(
    opdContent?.posteriorSegmentFundusExaminationDetail,
  );
  const posteriorRows = visibleRows(
    Object.entries(posteriorStructures).reduce(
      (rows: { label: string; od: string; os: string }[], [structure, label]) => {
        if (rows.some((row) => row.label === label)) return rows;
        const matchingDetails = posteriorDetails.filter(
          (detail: any) =>
            String(detail?.forStructure ?? "").toUpperCase() === structure,
        );
        rows.push({
          label,
          od: matchingDetails
            .filter((detail: any) => String(detail?.forEye).toUpperCase() === "OD")
            .map((detail: any) => valueText(detail?.dataPosteriorSegmentFundus))
            .filter(Boolean)
            .join("\n"),
          os: matchingDetails
            .filter((detail: any) => String(detail?.forEye).toUpperCase() === "OS")
            .map((detail: any) => valueText(detail?.dataPosteriorSegmentFundus))
            .filter(Boolean)
            .join("\n"),
        });
        return rows;
      },
      [],
    ),
  );

  const tables = [
    renderEyeTable("Visual Acuity", "Examination", visualAcuityRows),
    refractionTable,
    renderEyeTable("Slit Lamp", "Structure", visibleRows(slitLampRows)),
    renderEyeTable("IOP", "Parameter", iopRows),
    renderEyeTable(
      "Occular Motility and Pupils",
      "Parameter",
      ocularRows,
    ),
    renderEyeTable("Posterior Segment/ Fundus", "Structure", posteriorRows),
  ].filter(Boolean);

  return tables.length > 0 ? <div>{tables}</div> : null;
};

// Examination
const Examination = ({ opdContent }: { opdContent: any }) => {
  const examinationSummary = [
    {
      heading: "General Examination",
      items: [
        // Vital Parameters
        {
          label: "Temperature",
          value: opdContent?.vitalParameters?.temperatureByPalpationLabel,
        },
        {
          label: "Temperature Note",
          value: opdContent?.vitalParameters?.temperatureNote,
        },
        {
          label: "Pulse",
          value: opdContent?.vitalParameters?.pulseRateNote,
        },
        {
          label: "Respiratory Rate",
          value: opdContent?.vitalParameters?.respiratoryRateNote,
        },
        {
          label: "SpO2",
          value: opdContent?.vitalParameters?.spo2Note,
        },
        {
          label: "Blood pressure",
          value: opdContent?.vitalParameters?.bpNote,
        },
        {
          label: "Note",
          value: opdContent?.vitalParameters?.note,
        },

        // General Physical Examination
        {
          label: "Built",
          value: opdContent?.generalPhysicalExamination?.builtNote,
        },
        {
          label: "Nourishment",
          value: opdContent?.generalPhysicalExamination?.nourishmentNote,
        },
        {
          label: "Orientation to time place and person",
          value:
            opdContent?.generalPhysicalExamination
              ?.orientationToTimePlaceAndPersonLabel,
        },
        {
          label: "Orientation to time place and person Note",
          value:
            opdContent?.generalPhysicalExamination
              ?.orientationToTimePlaceAndPersonNote,
        },
        {
          label: "Pallor",
          value: opdContent?.generalPhysicalExamination?.anemia
            ? opdContent.generalPhysicalExamination.anemiaNote
              ? `+ ${opdContent.generalPhysicalExamination.anemiaNote}`
              : "+"
            : null,
        },
        {
          label: "Cyanosis",
          value: opdContent?.generalPhysicalExamination?.cyanosis
            ? opdContent.generalPhysicalExamination.cyanosisNote
              ? `+ ${opdContent.generalPhysicalExamination.cyanosisNote}`
              : "+"
            : null,
        },
        {
          label: "Clubbing",
          value: opdContent?.generalPhysicalExamination?.isGradeOfClubbing
            ? opdContent?.generalPhysicalExamination?.gradeOfClubbingLabel
              ? `+ ${opdContent?.generalPhysicalExamination?.gradeOfClubbingLabel}`
              : "+"
            : null,
        },
        {
          label: "Clubbing Note",
          value: opdContent?.generalPhysicalExamination?.gradeOfClubbingNote,
        },
        {
          label: "Jaundice",
          value: opdContent?.generalPhysicalExamination?.jaundice
            ? opdContent.generalPhysicalExamination.jaundiceNote
              ? `+ ${opdContent.generalPhysicalExamination.jaundiceNote}`
              : "+"
            : null,
        },
        {
          label: "Edema",
          value: opdContent?.generalPhysicalExamination?.isEdema
            ? opdContent.generalPhysicalExamination.edemaNote
              ? `+ ${opdContent.generalPhysicalExamination.edemaNote}`
              : "+"
            : null,
        },
        {
          label: "Lymphadenopathy",
          value: opdContent?.generalPhysicalExamination?.isLymphadenopathy
            ? opdContent?.generalPhysicalExamination?.lymphadenopathyNote
              ? `+ ${opdContent?.generalPhysicalExamination?.lymphadenopathyNote}`
              : "+"
            : null,
        },
        {
          label: "Pigmentation",
          value: opdContent?.generalPhysicalExamination?.isPigmentation
            ? opdContent?.generalPhysicalExamination?.pigmentationNote
              ? `+ ${opdContent?.generalPhysicalExamination?.pigmentationNote}`
              : "+"
            : null,
        },
        {
          label: "Scar",
          value: opdContent?.generalPhysicalExamination?.isAnyScarOnBody
            ? opdContent?.generalPhysicalExamination?.anyScarOnBodyNote
              ? `+ ${opdContent?.generalPhysicalExamination?.anyScarOnBodyNote}`
              : "+"
            : null,
        },
        {
          label: "Swelling",
          value: opdContent?.generalPhysicalExamination?.isAnySwellingOnBody
            ? opdContent?.generalPhysicalExamination?.anySwellingOnBodyNote
              ? `+ ${opdContent?.generalPhysicalExamination?.anySwellingOnBodyNote}`
              : "+"
            : null,
        },
        {
          label: "Lump",
          value: opdContent?.generalPhysicalExamination?.isLump
            ? opdContent?.generalPhysicalExamination?.lumpNote
              ? `+ ${opdContent?.generalPhysicalExamination?.lumpNote}`
              : "+"
            : null,
        },
        {
          label: "Scalp and Hair",
          value: opdContent?.generalPhysicalExamination?.scalpAndHairNote,
        },
        {
          label: "Eyes",
          value: opdContent?.generalPhysicalExamination?.eyesNote,
        },
        {
          label: "Ears",
          value: opdContent?.generalPhysicalExamination?.earsNote,
        },
        {
          label: "Nose",
          value: opdContent?.generalPhysicalExamination?.noseNote,
        },
        {
          label: "Mouth and Oral cavity",
          value: opdContent?.generalPhysicalExamination?.mouthAndOralCavityNote,
        },
        {
          label: "Tongue",
          value: opdContent?.generalPhysicalExamination?.tongueNote,
        },
        {
          label: "Teeth",
          value: opdContent?.generalPhysicalExamination?.teethNote,
        },
        {
          label: "Neck and Neck Glands",
          value: opdContent?.generalPhysicalExamination?.neckAndNeckGlandsNote,
        },
        {
          label: "Visible/distended veins in Neck",
          value:
            opdContent?.generalPhysicalExamination
              ?.anyVisibleOrDistendedVeinsInNeckNote,
        },
        {
          label: "JVP",
          value: opdContent?.generalPhysicalExamination?.jvpLabel,
        },
        {
          label: "JVP Note",
          value: opdContent?.generalPhysicalExamination?.jvpNote,
        },
        {
          label: "Chest",
          value: opdContent?.generalPhysicalExamination?.chestNote,
        },
        {
          label: "Back",
          value: opdContent?.generalPhysicalExamination?.backNote,
        },
        {
          label: "Abdomen",
          value: opdContent?.generalPhysicalExamination?.abdomenNote,
        },
        {
          label: "Upper extremities",
          value: opdContent?.generalPhysicalExamination?.upperExtremitiesNote,
        },
        {
          label: "Lower extremities",
          value: opdContent?.generalPhysicalExamination?.lowerExtremitiesNote,
        },
        {
          label: "Toe-Nails",
          value: opdContent?.generalPhysicalExamination?.toeNailsNote,
        },
        {
          label: "Chest deformity",
          value: opdContent?.generalPhysicalExamination?.isChestDeformity
            ? opdContent?.generalPhysicalExamination?.chestDeformityLabel
              ? `+ ${opdContent?.generalPhysicalExamination?.chestDeformityLabel}`
              : "+"
            : null,
        },
        {
          label: "Chest deformity Note",
          value: opdContent?.generalPhysicalExamination?.chestDeformityNote,
        },
        {
          label: "Limb deformity",
          value: opdContent?.generalPhysicalExamination?.isLimbDeformity
            ? opdContent?.generalPhysicalExamination?.limbDeformityLabel
              ? `+ ${opdContent?.generalPhysicalExamination?.limbDeformityLabel}`
              : "+"
            : null,
        },
        {
          label: "Limb deformity Note",
          value: opdContent?.generalPhysicalExamination?.limbDeformityNote,
        },
        {
          label: "External Genitalia",
          value: opdContent?.generalPhysicalExamination?.externalGenitaliaNote,
        },
        {
          label: "SMR (Sexual Maturity rating)",
          value:
            opdContent?.generalPhysicalExamination?.sexualMaturityRatingNote,
        },
        {
          label: "Other finding",
          value: opdContent?.generalPhysicalExamination?.anyOtherFindingNote,
        },
        {
          label: "Note",
          value: opdContent?.generalPhysicalExamination?.note,
        },
      ],
    },
    {
      heading: "Respiratory System",
      items: [
        // Inspection
        {
          label: "Respiratory rhythm",
          value: opdContent?.respiratoryInspection?.respiratoryRhythmNote,
        },
        {
          label: "Chest movements",
          value: opdContent?.respiratoryInspection?.chestMovementsNote,
        },
        {
          label: "Chest retractions",
          value: opdContent?.respiratoryInspection?.isChestRetractions
            ? opdContent?.respiratorySystemInspectionForChestRetractions
                ?.length > 0
              ? `+ ${opdContent.respiratorySystemInspectionForChestRetractions
                  ?.map((item) => item.siteOfChestRetractionsLabel)
                  .join(", ")}`
              : "+"
            : null,
        },
        {
          label: "Chest retractions Note",
          value: opdContent?.respiratoryInspection?.chestRetractionsNote,
        },
        {
          label: "Chest deformity",
          value: opdContent?.respiratoryInspection?.isAnyChestDeformity
            ? opdContent?.respiratoryInspection?.anyChestDeformityNote
              ? `+ ${opdContent?.respiratoryInspection?.anyChestDeformityNote}`
              : "+"
            : null,
        },
        {
          label: "Apex Impulse",
          value: opdContent?.respiratoryInspection?.apexImpulseNote,
        },
        {
          label: "Scar",
          value: opdContent?.respiratoryInspection?.isAnyScarOnBody
            ? opdContent?.respiratoryInspection?.anyScarOnBodyNote
              ? `+ ${opdContent?.respiratoryInspection?.anyScarOnBodyNote}`
              : "+"
            : null,
        },
        {
          label: "Swelling",
          value: opdContent?.respiratoryInspection?.isAnySwellingOnBody
            ? opdContent?.respiratoryInspection?.anySwellingOnBodyNote
              ? `+ ${opdContent?.respiratoryInspection?.anySwellingOnBodyNote}`
              : "+"
            : null,
        },
        {
          label: "Lump",
          value: opdContent?.respiratoryInspection?.isLump
            ? opdContent?.respiratoryInspection?.lumpNote
              ? `+ ${opdContent?.respiratoryInspection?.lumpNote}`
              : "+"
            : null,
        },
        {
          label: "Back",
          value: opdContent?.respiratoryInspection?.backNote,
        },
        {
          label: "Note",
          value: opdContent?.respiratoryInspection?.note,
        },

        // Palpation
        {
          label: "Chest movements",
          value: opdContent?.respiratoryPalpation?.chestMovementsNote,
        },
        {
          label: "Chest deformity",
          value: opdContent?.respiratoryPalpation?.isChestDeformity
            ? opdContent?.respiratoryPalpation?.chestDeformityNote
              ? `+ ${opdContent?.respiratoryPalpation?.chestDeformityNote}`
              : "+"
            : null,
        },
        {
          label: "Apex Impulse",
          value: opdContent?.respiratoryPalpation?.apexImpulseNote,
        },
        {
          label: "Scar",
          value: opdContent?.respiratoryPalpation?.isAnyScarOnBody
            ? opdContent?.respiratoryPalpation?.anyScarOnBodyNote
              ? `+ ${opdContent?.respiratoryPalpation?.anyScarOnBodyNote}`
              : "+"
            : null,
        },
        {
          label: "Swelling",
          value: opdContent?.respiratoryPalpation?.isAnySwellingOnBody
            ? opdContent?.respiratoryPalpation?.anySwellingOnBodyNote
              ? `+ ${opdContent?.respiratoryPalpation?.anySwellingOnBodyNote}`
              : "+"
            : null,
        },
        {
          label: "Lump",
          value: opdContent?.respiratoryPalpation?.isLump
            ? opdContent?.respiratoryPalpation?.lumpNote
              ? `+ ${opdContent?.respiratoryPalpation?.lumpNote}`
              : "+"
            : null,
        },
        {
          label: "Back",
          value: opdContent?.respiratoryPalpation?.backNote,
        },
        {
          label: "Tenderness",
          value: opdContent?.respiratoryPalpation?.isTendernessAtAnySite
            ? opdContent?.respiratoryPalpation?.tendernessAtAnySiteNote
              ? `+ ${opdContent?.respiratoryPalpation?.tendernessAtAnySiteNote}`
              : "+"
            : null,
        },
        {
          label: "Note",
          value: opdContent?.respiratoryPalpation?.note,
        },

        // Percussion
        {
          label: "Abnormal resonant",
          value: opdContent?.respiratoryPercussion?.abnormalResonantNote,
        },
        {
          label: "Cardiac dullness",
          value: opdContent?.respiratoryPercussion?.cardiacDullnessNote,
        },
        {
          label: "Liver dullness",
          value: opdContent?.respiratoryPercussion?.liverDullnessNote,
        },
        {
          label: "Other dull notes",
          value: opdContent?.respiratoryPercussion?.otherDullNotesNote,
        },
        {
          label: "Hyperresonant",
          value: opdContent?.respiratoryPercussion?.anyHyperresonantNotesNote,
        },
        {
          label: "Note",
          value: opdContent?.respiratoryPercussion?.note,
        },

        // Auscultation
        {
          label: "Breath sounds",
          value:
            opdContent?.respiratoryAuscultation
              ?.typeOfAbnormalBreathSoundsLabel,
        },
        {
          label: "Breath sounds Note",
          value: opdContent?.respiratoryAuscultation?.breathSoundsNote,
        },
        {
          label: "Adventitious sounds",
          value: opdContent?.respiratoryAuscultation?.isTypeOfAdventitiousSounds
            ? opdContent
                ?.respiratorySystemAuscultationForTypeOfAdventitiousSounds
                ?.length > 0
              ? `+ ${opdContent.respiratorySystemAuscultationForTypeOfAdventitiousSounds
                  ?.map((item) => item.typeOfAdventitiousSoundsLabel)
                  .join(", ")}`
              : "+"
            : null,
        },
        {
          label: "Adventitious sounds Note",
          value:
            opdContent?.respiratoryAuscultation?.typeOfAdventitiousSoundsNote,
        },
        {
          label: "Note",
          value: opdContent?.respiratoryAuscultation?.note,
        },
      ],
    },
    {
      heading: "Cardiovascular System",
      items: [
        // Inspection
        {
          label: "Respiratory rhythm",
          value:
            opdContent?.cardiovascularSystemInspection?.respiratoryRhythmNote,
        },
        {
          label: "Chest movements",
          value: opdContent?.cardiovascularSystemInspection?.chestMovementsNote,
        },
        {
          label: "Chest retractions",
          value: opdContent?.cardiovascularSystemInspection?.isChestRetractions
            ? opdContent?.cardiovascularSystemInspectionSiteOfChestRetraction
                ?.length > 0
              ? `+ ${opdContent.cardiovascularSystemInspectionSiteOfChestRetraction
                  ?.map((item) => item.siteOfChestRetractionsLabel)
                  .join(", ")}`
              : "+"
            : null,
        },
        {
          label: "Chest retractions Note",
          value:
            opdContent?.cardiovascularSystemInspection?.chestRetractionsNote,
        },
        {
          label: "Apex Impulse",
          value: opdContent?.cardiovascularSystemInspection?.apexImpulseNote,
        },
        {
          label: "Chest deformity",
          value: opdContent?.cardiovascularSystemInspection?.isAnyChestDeformity
            ? opdContent.cardiovascularSystemInspection.anyChestDeformityNote
              ? `+ ${opdContent.cardiovascularSystemInspection.anyChestDeformityNote}`
              : "+"
            : null,
        },
        {
          label: "Scar",
          value: opdContent?.cardiovascularSystemInspection?.isAnyScarOnBody
            ? opdContent?.cardiovascularSystemInspection?.anyScarOnBodyNote
              ? `+ ${opdContent?.cardiovascularSystemInspection?.anyScarOnBodyNote}`
              : "+"
            : null,
        },
        {
          label: "Swelling",
          value: opdContent?.cardiovascularSystemInspection?.isAnySwellingOnBody
            ? opdContent?.cardiovascularSystemInspection?.anySwellingOnBodyNote
              ? `+ ${opdContent?.cardiovascularSystemInspection?.anySwellingOnBodyNote}`
              : "+"
            : null,
        },
        {
          label: "Lump",
          value: opdContent?.cardiovascularSystemInspection?.isLump
            ? opdContent?.cardiovascularSystemInspection?.lumpNote
              ? `+ ${opdContent?.cardiovascularSystemInspection?.lumpNote}`
              : "+"
            : null,
        },
        {
          label: "Visible pulsations",
          value:
            opdContent?.cardiovascularSystemInspection
              ?.anyVisiblePulsationsNote,
        },
        {
          label: "Visible/prominent vesseles",
          value:
            opdContent?.cardiovascularSystemInspection
              ?.anyVisibleOrProminentVesselesNote,
        },
        {
          label: "Back",
          value: opdContent?.cardiovascularSystemInspection?.backNote,
        },
        {
          label: "Note",
          value: opdContent?.cardiovascularSystemInspection?.note,
        },

        // Palpation
        {
          label: "Chest Movements",
          value: opdContent?.cardiovasularSystemPalpation?.chestMovementsNote,
        },
        {
          label: "Chest Deformity",
          value: opdContent?.cardiovasularSystemPalpation?.isChestDeformity
            ? opdContent.cardiovasularSystemPalpation.chestDeformityNote
              ? `+ ${opdContent.cardiovasularSystemPalpation.chestDeformityNote}`
              : "+"
            : null,
        },
        {
          label: "Apex Impulse",
          value: opdContent?.cardiovasularSystemPalpation?.apexImpulseNote,
        },
        {
          label: "Palpable pulsations",
          value:
            opdContent?.cardiovasularSystemPalpation?.anyPalpablePulsationsNote,
        },
        {
          label: "Prominent/palpable veins",
          value:
            opdContent?.cardiovasularSystemPalpation
              ?.anyProminentOrPalpableVeinsNote,
        },
        {
          label: "Scar",
          value: opdContent?.cardiovasularSystemPalpation?.isAnyScarOnBody
            ? opdContent?.cardiovasularSystemPalpation?.anyScarOnBodyNote
              ? `+ ${opdContent?.cardiovasularSystemPalpation?.anyScarOnBodyNote}`
              : "+"
            : null,
        },
        {
          label: "Swelling",
          value: opdContent?.cardiovasularSystemPalpation?.isAnySwellingOnBody
            ? opdContent?.cardiovasularSystemPalpation?.anySwellingOnBodyNote
              ? `+ ${opdContent?.cardiovasularSystemPalpation?.anySwellingOnBodyNote}`
              : "+"
            : null,
        },
        {
          label: "Lump",
          value: opdContent?.cardiovasularSystemPalpation?.isLump
            ? opdContent?.cardiovasularSystemPalpation?.lumpNote
              ? `+ ${opdContent?.cardiovasularSystemPalpation?.lumpNote}`
              : "+"
            : null,
        },
        {
          label: "Back",
          value: opdContent?.cardiovasularSystemPalpation?.backNote,
        },
        {
          label: "Tenderness",
          value: opdContent?.cardiovasularSystemPalpation?.isTendernessAtAnySite
            ? opdContent?.cardiovasularSystemPalpation?.tendernessAtAnySiteNote
              ? `+ ${opdContent?.cardiovasularSystemPalpation?.tendernessAtAnySiteNote}`
              : "+"
            : null,
        },
        {
          label: "Note",
          value: opdContent?.cardiovasularSystemPalpation?.note,
        },

        // Percussion
        {
          label: "Abnormal Resonant",
          value:
            opdContent?.cardiovasularSystemPercussion?.abnormalResonantNote,
        },
        {
          label: "Cardiac Dullness",
          value: opdContent?.cardiovasularSystemPercussion?.cardiacDullnessNote,
        },
        {
          label: "Liver Dullness",
          value: opdContent?.cardiovasularSystemPercussion?.liverDullnessNote,
        },
        {
          label: "Other dull Notes",
          value: opdContent?.cardiovasularSystemPercussion?.isOtherDullNotes
            ? opdContent?.cardiovasularSystemPercussion?.otherDullNotesNote
              ? `+ ${opdContent?.cardiovasularSystemPercussion?.otherDullNotesNote}`
              : "+"
            : null,
        },
        {
          label: "Hyperresonant",
          value: opdContent?.cardiovasularSystemPercussion
            ?.isAnyHyperresonantNotes
            ? opdContent?.cardiovasularSystemPercussion
                ?.anyHyperresonantNotesNote
              ? `+ ${opdContent?.cardiovasularSystemPercussion?.anyHyperresonantNotesNote}`
              : "+"
            : null,
        },
        {
          label: "Note",
          value: opdContent?.cardiovasularSystemPercussion?.note,
        },

        // Auscultation
        {
          label: "Heart Sounds",
          value: opdContent?.cardiovasularSystemAuscultation?.heartSoundsLabel,
        },
        {
          label: "Heart Sounds Note",
          value: opdContent?.cardiovasularSystemAuscultation?.heartSoundsNote,
        },
        {
          label: "S1",
          value: opdContent?.cardiovasularSystemAuscultation?.s1Label,
        },
        {
          label: "S1 Note",
          value: opdContent?.cardiovasularSystemAuscultation?.s1Note,
        },
        {
          label: "S2",
          value: opdContent?.cardiovasularSystemAuscultation?.s2Label,
        },
        {
          label: "S2 Note",
          value: opdContent?.cardiovasularSystemAuscultation?.s2Note,
        },
        {
          label: "S3 gallop",
          value: opdContent?.cardiovasularSystemAuscultation?.isS3Gallop
            ? opdContent?.cardiovasularSystemAuscultation?.s3GallopNote
              ? `+ ${opdContent?.cardiovasularSystemAuscultation?.s3GallopNote}`
              : "+"
            : null,
        },
        {
          label: "S4",
          value: opdContent?.cardiovasularSystemAuscultation?.isS4
            ? opdContent?.cardiovasularSystemAuscultation?.s4Note
              ? `+ ${opdContent?.cardiovasularSystemAuscultation?.s4Note}`
              : "+"
            : null,
        },
        {
          label: "Murmurs",
          value:
            (opdContent?.cardiovasularSystemAuscultation?.timingOfMurmurLabel
              ? opdContent?.cardiovasularSystemAuscultation
                  ?.timingOfMurmurLabel + " "
              : "") +
            (opdContent?.cardiovasularSystemAuscultation?.murmursLabel ===
            "Present"
              ? "+"
              : ""),
        },
        {
          label: "Ejection Systolic murmur",
          value:
            opdContent?.cardiovasularSystemAuscultation
              ?.ejectionSystolicMurmurLabel === "Present"
              ? "+ " +
                [
                  opdContent?.cardiovasularSystemAuscultation?.gradeOfEsmLabel,
                  opdContent?.cardiovasularSystemAuscultation?.gradeOfEsmNote,
                ]
                  .filter(Boolean)
                  .join(", ")
              : null,
        },
        {
          label: "Pansystolic murmur",
          value:
            opdContent?.cardiovasularSystemAuscultation
              ?.pansystolicMurmurLabel === "Present"
              ? "+ " +
                [
                  opdContent?.cardiovasularSystemAuscultation?.gradeOfPsmLabel,
                  opdContent?.cardiovasularSystemAuscultation?.gradeOfPsmNote,
                ]
                  .filter(Boolean)
                  .join(", ")
              : null,
        },
        {
          label: "Diastolic Murmur",
          value:
            opdContent?.cardiovasularSystemAuscultation
              ?.diastolicMurmurLabel === "Present"
              ? "+ " +
                [
                  opdContent?.cardiovasularSystemAuscultation
                    ?.diastolicMurmurNote,
                ]
                  .filter(Boolean)
                  .join(", ")
              : null,
        },
        {
          label: "Venous Hum",
          value:
            opdContent?.cardiovasularSystemAuscultation?.venousHumLabel ===
            "Present"
              ? "+ " +
                [opdContent?.cardiovasularSystemAuscultation?.venousHumNote]
                  .filter(Boolean)
                  .join(", ")
              : null,
        },
        {
          label: "Other murmur",
          value:
            opdContent?.cardiovasularSystemAuscultation?.anyOtherMurmurLabel ===
            "Present"
              ? "+ " +
                [
                  opdContent?.cardiovasularSystemAuscultation
                    ?.anyOtherMurmurNote,
                ]
                  .filter(Boolean)
                  .join(", ")
              : null,
        },
        {
          label: "Other abnormal sounds",
          value: opdContent?.cardiovasularSystemAuscultation
            ?.isAnyOtherAbnormalSounds
            ? opdContent?.cardiovasularSystemAuscultation
                ?.anyOtherAbnormalSoundsNote
              ? `+ ${opdContent?.cardiovasularSystemAuscultation?.anyOtherAbnormalSoundsNote}`
              : "+"
            : null,
        },
        {
          label: "Note",
          value: opdContent?.cardiovasularSystemAuscultation?.note,
        },
      ],
    },
    {
      heading: "Per Abdomen",
      items: [
        // Inspection
        {
          label: "Shape of Abdomen",
          value: opdContent?.perAbdomenInspection?.shapeOfAbdomenNote,
        },
        {
          label: "Movements with Respiration",
          value: opdContent?.perAbdomenInspection?.movementsWithRespirationNote,
        },
        {
          label: "Umbilicus",
          value: opdContent?.perAbdomenInspection?.umbilicusNote,
        },
        {
          label: "Hernia",
          value: opdContent?.perAbdomenInspection?.isHernia
            ? opdContent?.perAbdomenInspectionForHernia?.length > 0
              ? `+ ${opdContent.perAbdomenInspectionForHernia
                  ?.map((item) => item.siteofHerniaLabel)
                  .join(", ")}`
              : "+"
            : null,
        },
        {
          label: "Hernia Note",
          value: opdContent?.perAbdomenInspection?.herniaNote,
        },
        {
          label: "Visible Vesseles",
          value:
            opdContent?.perAbdomenInspection?.anyVisibleOrProminentVesselesNote,
        },
        {
          label: "Scar",
          value: opdContent?.perAbdomenInspection?.isAnyScarOnBody
            ? opdContent?.perAbdomenInspection?.anyScarOnBodyNote
              ? `+ ${opdContent?.perAbdomenInspection?.anyScarOnBodyNote}`
              : "+"
            : null,
        },
        {
          label: "Swelling",
          value: opdContent?.perAbdomenInspection?.isAnySwellingOnBody
            ? opdContent?.perAbdomenInspection?.anySwellingOnBodyNote
              ? `+ ${opdContent?.perAbdomenInspection?.anySwellingOnBodyNote}`
              : "+"
            : null,
        },
        {
          label: "Lump",
          value: opdContent?.perAbdomenInspection?.isLump
            ? opdContent?.perAbdomenInspection?.lumpNote
              ? `+ ${opdContent?.perAbdomenInspection?.lumpNote}`
              : "+"
            : null,
        },
        {
          label: "Back",
          value: opdContent?.perAbdomenInspection?.backNote,
        },
        {
          label: "Note",
          value: opdContent?.perAbdomenInspection?.note,
        },

        // Palpation
        {
          label: "Movements with Respiration",
          value: opdContent?.perAbdomenPalpation?.movementsWithRespirationNote,
        },
        {
          label: "Liver",
          value:
            parseFloat(opdContent?.perAbdomenPalpation?.liverSpan || "0") > 0
              ? `Liver Span: ${parseFloat(
                  opdContent?.perAbdomenPalpation?.liverSpan || "0",
                )} cm below costal margin`
              : null,
        },
        {
          label: "Liver Tenderness",
          value:
            opdContent?.perAbdomenPalpation?.liverTendernessLabel === "Present"
              ? "+ "
              : null,
        },
        {
          label: "Liver Note",
          value: opdContent?.perAbdomenPalpation?.liverNote,
        },
        {
          label: "Spleen",
          value:
            parseFloat(opdContent?.perAbdomenPalpation?.spleenSize || "0") > 0
              ? `Spleen size: ${parseFloat(
                  opdContent?.perAbdomenPalpation?.spleenSize || "0",
                )} cm below costal margin`
              : null,
        },
        {
          label: "Spleen Tenderness",
          value:
            opdContent?.perAbdomenPalpation?.spleenTendernessLabel === "Present"
              ? "+ "
              : null,
        },
        {
          label: "Spleen Note",
          value: opdContent?.perAbdomenPalpation?.spleenNote,
        },
        {
          label: "Hernia",
          value: opdContent?.perAbdomenPalpation?.isHernia
            ? opdContent?.perAbdomenPalpationForHernia?.length > 0
              ? `+ ${opdContent.perAbdomenPalpationForHernia
                  ?.map((item) => item.siteofHerniaLabel)
                  .join(", ")}`
              : "+"
            : null,
        },
        {
          label: "Hernia Note",
          value: opdContent?.perAbdomenPalpation?.herniaNote,
        },
        {
          label: "Ascites",
          value: opdContent?.perAbdomenPalpation?.ascitesNote,
        },
        {
          label: "Scar",
          value: opdContent?.perAbdomenPalpation?.isAnyScarOnBody
            ? opdContent?.perAbdomenPalpation?.anyScarOnBodyNote
              ? `+ ${opdContent?.perAbdomenPalpation?.anyScarOnBodyNote}`
              : "+"
            : null,
        },
        {
          label: "Swelling",
          value: opdContent?.perAbdomenPalpation?.isAnySwellingOnBody
            ? opdContent?.perAbdomenPalpation?.anySwellingOnBodyNote
              ? `+ ${opdContent?.perAbdomenPalpation?.anySwellingOnBodyNote}`
              : "+"
            : null,
        },
        {
          label: "Lump",
          value: opdContent?.perAbdomenPalpation?.isLump
            ? opdContent?.perAbdomenPalpation?.lumpNote
              ? `+ ${opdContent?.perAbdomenPalpation?.lumpNote}`
              : "+"
            : null,
        },
        {
          label: "Back",
          value: opdContent?.perAbdomenPalpation?.backNote,
        },
        {
          label: "Tenderness",
          value: opdContent?.perAbdomenPalpation?.isTendernessAtAnySite
            ? opdContent?.perAbdomenPalpation?.tendernessAtAnySiteNote
              ? `+ ${opdContent?.perAbdomenPalpation?.tendernessAtAnySiteNote}`
              : "+"
            : null,
        },
        {
          label: "Rebound Tenderness",
          value: opdContent?.perAbdomenPalpation?.isReboundTendernessAtAnySite
            ? opdContent?.perAbdomenPalpation?.reboundTendernessAtAnySiteNote
              ? `+ ${opdContent?.perAbdomenPalpation?.reboundTendernessAtAnySiteNote}`
              : "+"
            : null,
        },
        {
          label: "Note",
          value: opdContent?.perAbdomenPalpation?.note,
        },

        // Percussion
        {
          label: "Abnormal Resonant",
          value: opdContent?.perAbdomenPercussion?.isAbnormalResonant
            ? opdContent?.perAbdomenPercussion?.abnormalResonantNote
              ? `+ ${opdContent.perAbdomenPercussion.abnormalResonantNote}`
              : "+"
            : null,
        },
        {
          label: "Tympanic",
          value: opdContent?.perAbdomenPercussion?.tympanicNoteLabel,
        },
        {
          label: "Tympanic Note",
          value: opdContent?.perAbdomenPercussion?.tympanicNoteNote,
        },
        {
          label: "Abnormal Dullness",
          value: opdContent?.perAbdomenPercussion?.isAbnormalDullnessAtAnySite
            ? opdContent?.perAbdomenPercussion?.abnormalDullnessAtAnySiteNote
              ? `+ ${opdContent?.perAbdomenPercussion?.abnormalDullnessAtAnySiteNote}`
              : "+"
            : null,
        },
        {
          label: "Liver Dullness",
          value: opdContent?.perAbdomenPercussion?.liverDullnessLabel,
        },
        {
          label: "Liver Dullness Note",
          value: opdContent?.perAbdomenPercussion?.liverDullnessNote,
        },
        {
          label: "Other Dull Notes",
          value: opdContent?.perAbdomenPercussion?.isOtherDullNotes
            ? opdContent?.perAbdomenPercussion?.otherDullNotesNote
              ? `+ ${opdContent?.perAbdomenPercussion?.otherDullNotesNote}`
              : "+"
            : null,
        },
        {
          label: "Free fluid",
          value: opdContent?.perAbdomenPercussion?.isAnyFreeFluid
            ? opdContent?.perAbdomenPercussion?.anyFreeFluidNote
              ? `+ ${opdContent.perAbdomenPercussion.anyFreeFluidNote}`
              : "+"
            : null,
        },
        {
          label: "Note",
          value: opdContent?.perAbdomenPercussion?.note,
        },

        // Auscultation
        {
          label: "Peristalsis Sounds",
          value: opdContent?.perAbdomenAuscultation?.peristalsisSoundsLabel,
        },
        {
          label: "Peristalsis Sounds",
          value: opdContent?.perAbdomenAuscultation?.isPeristalsisSounds
            ? opdContent?.perAbdomenAuscultation?.peristalsisSoundsNote
              ? `+ ${opdContent.perAbdomenAuscultation.peristalsisSoundsNote}`
              : "+"
            : null,
        },
        {
          label: "Rubs",
          value: opdContent?.perAbdomenAuscultation?.isRubs
            ? opdContent?.perAbdomenAuscultation?.rubsNote
              ? `+ ${opdContent.perAbdomenAuscultation.rubsNote}`
              : "+"
            : null,
        },
        {
          label: "Per Rectal Examination",
          value: opdContent?.perAbdomenAuscultation?.perRectalExaminationLabel,
        },
        {
          label: "Per Rectal Examination Note",
          value: opdContent?.perAbdomenAuscultation?.perRectalExaminationNote,
        },
        {
          label: "Note",
          value: opdContent?.perAbdomenAuscultation?.note,
        },
      ],
    },
    {
      heading: "Central Nervous System",
      items: [
        // Higher Functions
        { subHeading: "Higher Functions" },
        {
          label: "Stage of Consciousness",
          value: opdContent?.cnsHigherFunctions?.stageOfConsciousnessLabel,
        },
        {
          label: "Stage of Consciousness Note",
          value: opdContent?.cnsHigherFunctions?.stageOfConsciousnessNote,
        },
        {
          label: "Memory",
          value: opdContent?.cnsHigherFunctions?.memoryLabel,
        },
        {
          label: "Memory Note",
          value: opdContent?.cnsHigherFunctions?.memoryNote,
        },
        {
          label: "Orientation to Time",
          value: opdContent?.cnsHigherFunctions?.orientationToTimeLabel,
        },
        {
          label: "Orientation to Time Note",
          value: opdContent?.cnsHigherFunctions?.orientationToTimeNote,
        },
        {
          label: "Orientation to Place",
          value: opdContent?.cnsHigherFunctions?.orientationToPlaceLabel,
        },
        {
          label: "Orientation to Place Note",
          value: opdContent?.cnsHigherFunctions?.orientationToPlaceNote,
        },
        {
          label: "Orientation to Person",
          value: opdContent?.cnsHigherFunctions?.orientationToPersonLabel,
        },
        {
          label: "Orientation to Person Note",
          value: opdContent?.cnsHigherFunctions?.orientationToPersonNote,
        },
        {
          label: "Speech",
          value: opdContent?.cnsHigherFunctions?.speechLabel,
        },
        {
          label: "Speech Note",
          value: opdContent?.cnsHigherFunctions?.speechNote,
        },
        {
          label: "General Behaviour",
          value: opdContent?.cnsHigherFunctions?.generalBehaviourLabel,
        },
        {
          label: "General Behaviour Note",
          value: opdContent?.cnsHigherFunctions?.generalBehaviourNote,
        },
        {
          label: "Hallucinations",
          value: opdContent?.cnsHigherFunctions?.isHallucinations
            ? opdContent?.cnsHigherFunctions?.hallucinationsNote
              ? `+ ${opdContent.cnsHigherFunctions.hallucinationsNote}`
              : "+"
            : null,
        },
        {
          label: "Delusions",
          value: opdContent?.cnsHigherFunctions?.isDelusions
            ? opdContent?.cnsHigherFunctions?.delusionsNote
              ? `+ ${opdContent.cnsHigherFunctions.delusionsNote}`
              : "+"
            : null,
        },
        {
          label: "Gait",
          value: opdContent?.cnsHigherFunctions?.gaitLabel,
        },
        {
          label: "Gait Note",
          value: opdContent?.cnsHigherFunctions?.gaitNote,
        },
        {
          label: "Note",
          value: opdContent?.cnsHigherFunctions?.note,
        },

        // Motor System
        { subHeading: "Motor System" },
        {
          label: "Muscle Bulk",
          value: opdContent?.cnsMotorSystem?.muscleBulkLabel,
        },
        {
          label: "Muscle Bulk Note",
          value: opdContent?.cnsMotorSystem?.muscleBulkNote,
        },
        {
          label: "Muscle Power Grade",
          value: opdContent?.cnsMotorSystem?.musclePowerGradeLabel,
        },
        {
          label: "Muscle Power Grade Note",
          value: opdContent?.cnsMotorSystem?.musclePowerGradeNote,
        },
        {
          label: "Muscle Tone",
          value: opdContent?.cnsMotorSystem?.muscleToneLabel,
        },
        {
          label: "Muscle Tone Note",
          value: opdContent?.cnsMotorSystem?.muscleToneNote,
        },
        {
          label: "Co-ordination",
          value: opdContent?.cnsMotorSystem?.coordinationLabel,
        },
        {
          label: "Co-ordination Note",
          value: opdContent?.cnsMotorSystem?.coordinationNote,
        },
        {
          label: "Finger Nose Test",
          value: opdContent?.cnsMotorSystem?.fingerNoseTestLabel,
        },
        {
          label: "Finger Nose Test Note",
          value: opdContent?.cnsMotorSystem?.fingerNoseTestNote,
        },
        {
          label: "Finger Test",
          value: opdContent?.cnsMotorSystem?.fingerTestLabel,
        },
        {
          label: "Finger Test Note",
          value: opdContent?.cnsMotorSystem?.fingerTestNote,
        },
        {
          label: "Knee-heal Test",
          value: opdContent?.cnsMotorSystem?.kneeHealTestLabel,
        },
        {
          label: "Knee-heal Test Note",
          value: opdContent?.cnsMotorSystem?.kneeHealTestNote,
        },
        {
          label: "Involuntary Movements",
          value:
            opdContent?.motorSystemForInvoluntarymovements &&
            opdContent?.motorSystemForInvoluntarymovements?.length > 0
              ? `${opdContent.motorSystemForInvoluntarymovements
                  ?.map((item) => item.involuntaryMovementsLabel)
                  .join(", ")}`
              : null,
        },
        {
          label: "Involuntary Movements Note",
          value: opdContent?.cnsMotorSystem?.involuntaryMovementsNote,
        },
        {
          label: "Note",
          value: opdContent?.cnsMotorSystem?.note,
        },

        // Sensory System
        { subHeading: "Sensory System" },
        {
          label: "Fine Touch Perception",
          value: opdContent?.cnsSensorySystem?.fineTouchPerceptionLabel,
        },
        {
          label: "Fine Touch Perception Note",
          value: opdContent?.cnsSensorySystem?.fineTouchPerceptionNote,
        },
        {
          label: "Crude Touch Perception",
          value: opdContent?.cnsSensorySystem?.crudeTouchPerceptionLabel,
        },
        {
          label: "Crude Touch Perception Note",
          value: opdContent?.cnsSensorySystem?.crudeTouchPerceptionNote,
        },
        {
          label: "Pain Perception",
          value: opdContent?.cnsSensorySystem?.painPerceptionLabel,
        },
        {
          label: "Pain Perception Note",
          value: opdContent?.cnsSensorySystem?.painPerceptionNote,
        },
        {
          label: "Temperature Perception",
          value: opdContent?.cnsSensorySystem?.temperaturePerceptionLabel,
        },
        {
          label: "Temperature Perception Note",
          value: opdContent?.cnsSensorySystem?.temperaturePerceptionLabel,
        },
        {
          label: "Joint Sense",
          value: opdContent?.cnsSensorySystem?.jointSenseLabel,
        },
        {
          label: "Joint Sense Note",
          value: opdContent?.cnsSensorySystem?.jointSenseNote,
        },
        {
          label: "Tactile Localization",
          value: opdContent?.cnsSensorySystem?.tactileLocalizationLabel,
        },
        {
          label: "Tactile Localization Note",
          value: opdContent?.cnsSensorySystem?.tactileLocalizationNote,
        },
        {
          label: "Tactile Discrimination",
          value: opdContent?.cnsSensorySystem?.tactileDiscriminationLabel,
        },
        {
          label: "Tactile Discrimination Note",
          value: opdContent?.cnsSensorySystem?.tactileDiscriminationNote,
        },
        {
          label: "Tactile Extinction",
          value: opdContent?.cnsSensorySystem?.tactileExtinctionLabel,
        },
        {
          label: "Tactile Extinction Note",
          value: opdContent?.cnsSensorySystem?.tactileExtinctionNote,
        },
        {
          label: "Cerebellar Signs",
          value:
            opdContent?.sensorySystemForCerebellarSigns &&
            opdContent?.sensorySystemForCerebellarSigns?.length > 0
              ? `${opdContent.sensorySystemForCerebellarSigns
                  ?.map((item) => item.cerebellarSignsLabel)
                  .join(", ")}`
              : null,
        },
        {
          label: "Cerebellar Signs Note",
          value: opdContent?.cnsSensorySystem?.cerebellarSignsNote,
        },
        {
          label: "Meningeal Signs",
          value:
            opdContent?.sensorySystemForMeningealSigns &&
            opdContent?.sensorySystemForMeningealSigns?.length > 0
              ? `${opdContent.sensorySystemForMeningealSigns
                  ?.map((item) => item.meningealSignsLabel)
                  .join(", ")}`
              : null,
        },
        {
          label: "Meningeal Signs Note",
          value: opdContent?.cnsSensorySystem?.meningealSignsNote,
        },
        {
          label: "Glabellar Tap",
          value: opdContent?.cnsSensorySystem?.glabellarTapLabel,
        },
        {
          label: "Glabellar Tap Note",
          value: opdContent?.cnsSensorySystem?.glabellarTapNote,
        },
        {
          label: "Corneal Reflex",
          value: opdContent?.cnsSensorySystem?.cornealReflexLabel,
        },
        {
          label: "Corneal Reflex Note",
          value: opdContent?.cnsSensorySystem?.cornealReflexNote,
        },
        {
          label: "Conjuctival Reflex",
          value: opdContent?.cnsSensorySystem?.conjuctivalReflexLabel,
        },
        {
          label: "Conjuctival Reflex Note",
          value: opdContent?.cnsSensorySystem?.conjuctivalReflexNote,
        },
        {
          label: "Palatal Reflex",
          value: opdContent?.cnsSensorySystem?.palatalReflexLabel,
        },
        {
          label: "Palatal Reflex Note",
          value: opdContent?.cnsSensorySystem?.palatalReflexNote,
        },
        {
          label: "Pharygeal Reflex",
          value: opdContent?.cnsSensorySystem?.pharygealReflexLabel,
        },
        {
          label: "Pharygeal Reflex Note",
          value: opdContent?.cnsSensorySystem?.pharygealReflexNote,
        },
        {
          label: "Abdominal Reflex",
          value: opdContent?.cnsSensorySystem?.abdominalReflexLabel,
        },
        {
          label: "Abdominal Reflex Note",
          value: opdContent?.cnsSensorySystem?.abdominalReflexNote,
        },
        {
          label: "Planter Reflex",
          value: opdContent?.cnsSensorySystem?.planterReflexLabel,
        },
        {
          label: "Planter Reflex Note",
          value: opdContent?.cnsSensorySystem?.planterReflexNote,
        },
        {
          label: "Jaw Jerk",
          value: opdContent?.cnsSensorySystem?.jawJerkLabel,
        },
        {
          label: "Jaw Jerk Note",
          value: opdContent?.cnsSensorySystem?.jawJerkNote,
        },
        {
          label: "Biceps Jerk",
          value: opdContent?.cnsSensorySystem?.bicepsJerkLabel,
        },
        {
          label: "Biceps Jerk Note",
          value: opdContent?.cnsSensorySystem?.bicepsJerkNote,
        },
        {
          label: "Tricips Jerk",
          value: opdContent?.cnsSensorySystem?.tricipsJerkLabel,
        },
        {
          label: "Tricips Jerk Note",
          value: opdContent?.cnsSensorySystem?.tricipsJerkNote,
        },
        {
          label: "Knee Reflex",
          value: opdContent?.cnsSensorySystem?.kneeReflexLabel,
        },
        {
          label: "Knee Reflex Note",
          value: opdContent?.cnsSensorySystem?.kneeReflexNote,
        },
        {
          label: "Ankle Reflex",
          value: opdContent?.cnsSensorySystem?.ankleReflexLabel,
        },
        {
          label: "Ankle Reflex Note",
          value: opdContent?.cnsSensorySystem?.ankleReflexNote,
        },
        {
          label: "Note",
          value: opdContent?.cnsSensorySystem?.note,
        },

        // Cranial Nerves
        { subHeading: "Cranial Nerves" },
        {
          label: "Cranial nerve 1",
          value: opdContent?.cnsCranialNerves?.cranialNerve1Label,
        },
        {
          label: "Cranial nerve 1 Note",
          value: opdContent?.cnsCranialNerves?.cranialNerve1Note,
        },
        {
          label: "Cranial nerve 2",
          value: opdContent?.cnsCranialNerves?.cranialNerve2Label,
        },
        {
          label: "Cranial nerve 2 Note",
          value: opdContent?.cnsCranialNerves?.cranialNerve2Note,
        },
        {
          label: "Cranial nerve 3",
          value: opdContent?.cnsCranialNerves?.cranialNerve3Label,
        },
        {
          label: "Cranial nerve 3 Note",
          value: opdContent?.cnsCranialNerves?.cranialNerve3Note,
        },
        {
          label: "Cranial nerve 4",
          value: opdContent?.cnsCranialNerves?.cranialNerve4Label,
        },
        {
          label: "Cranial nerve 4 Note",
          value: opdContent?.cnsCranialNerves?.cranialNerve4Note,
        },
        {
          label: "Cranial nerve 5",
          value: opdContent?.cnsCranialNerves?.cranialNerve5Label,
        },
        {
          label: "Cranial nerve 5 Note",
          value: opdContent?.cnsCranialNerves?.cranialNerve5Note,
        },
        {
          label: "Cranial nerve 6",
          value: opdContent?.cnsCranialNerves?.cranialNerve6Label,
        },
        {
          label: "Cranial nerve 6 Note",
          value: opdContent?.cnsCranialNerves?.cranialNerve6Note,
        },
        {
          label: "Cranial nerve 7",
          value: opdContent?.cnsCranialNerves?.cranialNerve7Label,
        },
        {
          label: "Cranial nerve 7 Note",
          value: opdContent?.cnsCranialNerves?.cranialNerve7Note,
        },
        {
          label: "Cranial nerve 8",
          value: opdContent?.cnsCranialNerves?.cranialNerve8Label,
        },
        {
          label: "Cranial nerve 8 Note",
          value: opdContent?.cnsCranialNerves?.cranialNerve8Note,
        },
        {
          label: "Cranial nerve 9",
          value: opdContent?.cnsCranialNerves?.cranialNerve9Label,
        },
        {
          label: "Cranial nerve 9 Note",
          value: opdContent?.cnsCranialNerves?.cranialNerve9Note,
        },
        {
          label: "Cranial nerve 10",
          value: opdContent?.cnsCranialNerves?.cranialNerve10Label,
        },
        {
          label: "Cranial nerve 10 Note",
          value: opdContent?.cnsCranialNerves?.cranialNerve10Note,
        },
        {
          label: "Cranial nerve 11",
          value: opdContent?.cnsCranialNerves?.cranialNerve11Label,
        },
        {
          label: "Cranial nerve 11 Note",
          value: opdContent?.cnsCranialNerves?.cranialNerve11Note,
        },
        {
          label: "Cranial nerve 12",
          value: opdContent?.cnsCranialNerves?.cranialNerve12Label,
        },
        {
          label: "Cranial nerve 12 Note",
          value: opdContent?.cnsCranialNerves?.cranialNerve12Note,
        },
        {
          label: "Note",
          value: opdContent?.cnsCranialNerves?.note,
        },
      ],
    },
    {
      heading: "Examination Summary",
      items: [
        {
          label: "Note",
          value: opdContent?.examinationSummary?.note,
        },
      ],
    },
  ];

  const hasDetails = examinationSummary.some((section) =>
    section.items?.some((item) =>
      item?.value
        ?.toString()
        ?.replace(/<\/?(div|p|br)[^>]*>/gi, "")
        ?.replace(/\n|\r/g, "")
        ?.replace(/\s+/g, " ")
        ?.trim(),
    ),
  );

  const hasOphthalData = hasOphthalExaminationData(opdContent);

  if (!hasDetails && !hasOphthalData) return null;

  return (
    <div style={styles.container}>
      <div style={styles.cellContainer}>
        <div style={{ ...styles.cell, fontSize: "10px" }}>EXAMINATION</div>
      </div>

      <div style={{ padding: "5px", fontSize: "11px" }} className="content">
        {examinationSummary?.map((section, sectionIndex) => {
          // const details = section?.items?.filter((item) => {
          //   const detailNote = item?.value
          //     ?.toString()
          //     ?.replace(/<\/?(div|p|br)[^>]*>/gi, '')
          //     ?.replace(/\n|\r/g, '')
          //     ?.replace(/\s+/g, ' ')
          //     ?.trim();
          //   return detailNote || 'subHeading' in item;
          // });

          const details = section?.items?.filter((item, index) => {
            if ("subHeading" in item) {
              for (let i = index + 1; i < section.items.length; i++) {
                const nextItem = section.items[i];
                if ("subHeading" in nextItem) break;

                const nextValue = nextItem?.value
                  ?.toString()
                  ?.replace(/<\/?(div|p|br)[^>]*>/gi, "")
                  ?.replace(/\n|\r/g, "")
                  ?.replace(/\s+/g, " ")
                  ?.trim();

                if (nextValue) return true;
              }
              return false;
            }

            const note = item?.value
              ?.toString()
              ?.replace(/<\/?(div|p|br)[^>]*>/gi, "")
              ?.replace(/\n|\r/g, "")
              ?.replace(/\s+/g, " ")
              ?.trim();

            return !!note;
          });

          const showOphthalTables =
            section?.heading === "Examination Summary" && hasOphthalData;
          if ((!details || details.length === 0) && !showOphthalTables) {
            return null;
          }

          return (
            <div key={sectionIndex}>
              {showOphthalTables && (
                <OphthalExaminationTables opdContent={opdContent} />
              )}
              <span
                className="bold-600"
                style={{
                  textDecoration: "underline",
                  marginRight:
                    section?.heading !== "Examination Summary" ? "10px" : "0",
                }}
              >
                {section?.heading !== "Examination Summary" &&
                  section?.heading?.toUpperCase()}
              </span>

              {details?.map((item, index) => {
                if ("subHeading" in item) {
                  let hasDetailsAfterSubHeading = false;

                  for (let i = index + 1; i < details.length; i++) {
                    const nextItem = details[i];
                    if ("subHeading" in nextItem) break;

                    const note = nextItem?.value
                      ?.toString()
                      ?.replace(/<\/?(div|p|br)[^>]*>/gi, "")
                      ?.replace(/\n|\r/g, "")
                      ?.replace(/\s+/g, " ")
                      ?.trim();

                    if (note) {
                      hasDetailsAfterSubHeading = true;
                      break;
                    }
                  }

                  if (!hasDetailsAfterSubHeading) return null;

                  return (
                    <div
                      key={index}
                      className="bold-600"
                      style={{
                        textDecoration: "underline",
                        marginRight: "10px",
                      }}
                    >
                      {item.subHeading}
                    </div>
                  );
                }

                const detailNote = item?.value
                  ?.toString()
                  ?.replace(/<\/?(div|p|br)[^>]*>/gi, "")
                  ?.replace(/\n|\r/g, "")
                  ?.replace(/\s+/g, " ")
                  ?.trim();

                const withoutLabelNotes = [
                  "Note",
                  "Temperature Note",
                  "Orientation to time place and person Note",
                  "Clubbing Note",
                  "JVP Note",
                  "Chest deformity Note",
                  "Limb deformity Note",
                  "Chest retractions Note",
                  "Breath sounds Note",
                  "Adventitious sounds Note",
                  "Chest retractions Note",
                  "Heart Sounds Note",
                  "S1 Note",
                  "S2 Note",
                  "S3 gallop Note",
                  "Hernia Note",
                  "Liver Note",
                  "Spleen Note",
                  "Abnormal Resonant Note",
                  "Tympanic Note",
                  "Abnormal Dullness at Any Site Note",
                  "Liver Dullness Note",
                  "Other Dull Note",
                  "Any free fluid Note",
                  "Peristalsis Sounds Note",
                  "Rubs Note",
                  "Per Rectal Examination Note",
                  "Stage of Consciousness Note",
                  "Memory Note",
                  "Orientation to Time Note",
                  "Orientation to Place Note",
                  "Orientation to Person Note",
                  "Speech Note",
                  "General Behaviour Note",
                  "Hallucinations Note",
                  "Delusions Note",
                  "Gait Note",
                  "Muscle Bulk Note",
                  "Muscle Power Grade Note",
                  "Co-ordination Note",
                  "Finger Nose Test Note",
                  "Finger Test Note",
                  "Knee-heal Test Note",
                  "Involuntary Movements Note",
                  "Fine Touch Perception Note",
                  "Crude Touch Perception Note",
                  "Pain Perception Note",
                  "Temperature Perception Note",
                  "Joint Sense Note",
                  "Tactile Localization Note",
                  "Tactile Discrimination Note",
                  "Tactile Extinction Note",
                  "Cerebellar Signs Note",
                  "Meningeal Signs Note",
                  "Glabellar Tap Note",
                  "Corneal Reflex Note",
                  "Conjuctival Reflex Note",
                  "Palatal Reflex Note",
                  "Pharygeal Reflex Note",
                  "Abdominal Reflex Note",
                  "Planter Reflex Note",
                  "Jaw Jerk Note",
                  "Biceps Jerk Note",
                  "Tricips Jerk Note",
                  "Knee Reflex Note",
                  "Ankle Reflex Note",
                  "Cranial nerve 1 Note",
                  "Cranial nerve 2 Note",
                  "Cranial nerve 3 Note",
                  "Cranial nerve 4 Note",
                  "Cranial nerve 5 Note",
                  "Cranial nerve 6 Note",
                  "Cranial nerve 7 Note",
                  "Cranial nerve 8 Note",
                  "Cranial nerve 9 Note",
                  "Cranial nerve 10 Note",
                  "Cranial nerve 11 Note",
                  "Cranial nerve 12 Note",
                ].includes(item?.label);

                return withoutLabelNotes ? (
                  <span key={index} style={{ marginBottom: "2px" }}>
                    {parse(detailNote)};{" "}
                  </span>
                ) : item?.label === "Liver" || item?.label === "Spleen" ? (
                  <>
                    <u>
                      <b>{item?.label}:</b>
                    </u>{" "}
                    {parse(detailNote)};{" "}
                  </>
                ) : (
                  <span key={index} style={{ marginBottom: "2px" }}>
                    <span className="bold-600">{item?.label}:</span>{" "}
                    {parse(detailNote)};{" "}
                  </span>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Vaccination Component
const Vaccination = ({ opdContent }: { opdContent: any }) => {
  return (
    <div style={styles.container}>
      <div style={styles.cellContainer}>
        <div style={{ ...styles.cell, fontSize: "10px" }}>VACCINES GIVEN</div>
      </div>
      <div style={{ padding: "5px", fontSize: "11px" }} className="content">
        <div>
          {opdContent?.patientVaccinations?.map((vacc: any, index: number) => (
            <div key={index} style={{ fontWeight: 600 }}>
              {vacc?.brandName}
              {vacc?.genericName ? ` (${vacc?.genericName})` : ""}{" "}
              {vacc?.doseName}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Investigation Component
const Investigation = ({ opdContent }: { opdContent: any }) => {
  return (
    <div style={styles.container}>
      <div style={styles.cellContainer}>
        <div style={{ ...styles.cell, fontSize: "10px" }}>INVESTIGATIONS</div>
      </div>
      <div style={{ fontSize: "11px" }}>
        <div>
          <div style={{ padding: "5px" }}>
            <div>
              {opdContent?.investigations?.map((item: any, index: number) => (
                <span key={index}>
                  {index === 0 ? "" : ", "}
                  <span style={{ fontWeight: 600 }}>
                    {item?.investigationName}
                  </span>
                  {item?.investigationDetailNote &&
                    `, ${item.investigationDetailNote.replace(/<[^>]+>/g, "")}`}
                </span>
              ))}
            </div>
          </div>
          <div style={{ paddingLeft: "5px" }}>
            {parse(
              opdContent?.investigationNotes[0]?.note
                ?.replace(/^<p>/, "")
                ?.replace(/<\/p>$/, ""),
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Procedure
const Procedure = ({ opdContent }: { opdContent: any }) => {
  const stripHtml = (html: string) =>
    html ? html.replace(/<[^>]*>/g, "").trim() : "";

  const memberProcedures = opdContent?.memberProcedure ?? [];
  const procedures = opdContent?.memberProcedureProcedure ?? [];
  const procedureDetails =
    opdContent?.memberProcedureProcedureDetail
      ?.slice()
      ?.sort(
        (a: any, b: any) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0),
      ) ?? [];
  const visits = opdContent?.memberProcedureProcedureDetailVisit ?? [];
  const visitBodyParts =
    opdContent?.memberProcedureProcedureDetailVisitMappedBodyPart ?? [];

  // Collect distinct memberProcedureIds to render separate boxes
  const memberIds = [
    ...new Set([
      ...procedures.map((p: any) => p.memberProcedureId),
      ...procedureDetails.map((p: any) => p.memberProcedureId),
      ...memberProcedures.map((m: any) => m.memberProcedureId),
    ]),
  ].filter(Boolean);

  const renderBodyParts = (parts: any[]) => {
    const seen = new Set<string>();
    return parts
      .map((bp: any) => {
        const key = `${bp.bodyPartLabel ?? ""}|${bp.bodyPartName ?? ""}`;
        if (seen.has(key)) return null;
        seen.add(key);
        return [bp.bodyPartLabel, bp.bodyPartName].filter(Boolean).join(" ");
      })
      .filter(Boolean)
      .join(", ");
  };

  const getUniqueBodyPartRows = (parts: any[]) => {
    const seen = new Set<string>();
    return parts.filter((bp: any) => {
      const key = `${bp?.bodyPartLabel ?? ""}|${bp?.bodyPartName ?? ""}`;
      if (!key.trim() || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const getMemberProcedureMeta = (memberProcedureId: any) => {
    const memberProcedure = memberProcedures.find(
      (mp: any) => mp.memberProcedureId === memberProcedureId,
    );

    return {
      isGeneral: !!memberProcedure?.isGeneral,
      generalProcedureName: stripHtml(
        memberProcedure?.generalProcedureName || "",
      ),
    };
  };

  const lineJoin = (chunks: (string | undefined)[]) =>
    chunks.filter(Boolean).join("  •  ");

  const getCommonProcedureNotes = (memberProcedureId: any) => {
    const seen = new Set<string>();

    return procedures
      .filter(
        (procedure: any) =>
          procedure.memberProcedureId === memberProcedureId &&
          procedure.isNoteEnabled,
      )
      .map((procedure: any) => stripHtml(procedure.note || ""))
      .filter((note: string) => {
        if (!note || seen.has(note)) return false;
        seen.add(note);
        return true;
      });
  };

  const hasRenderableProcedureDetail = (detail: any) => {
    const detailVisits = visits.filter(
      (visit: any) =>
        visit.memberProcedureProcedureDetailId ===
        detail.memberProcedureProcedureDetailId,
    );

    const isDetailCompleted =
      (detail.processStatus || "").toLowerCase() === "completed";
    const hasCompletedVisit = detailVisits.some(
      (visit: any) => (visit.processStatus || "").toLowerCase() === "completed",
    );

    return isDetailCompleted || hasCompletedVisit;
  };

  if (memberIds.length === 0 && procedureDetails.length === 0) {
    return null;
  }

  const boxStyle: React.CSSProperties = {
    border: "1px solid #d9d9d9",
    borderRadius: "10px",
    padding: "10px 12px",
    marginBottom: "12px",
    background: "#fdfdfd",
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  };

  const renderMemberBlock = (memberId: any, memberIndex: number) => {
    const pDetails = procedureDetails.filter(
      (p: any) => p.memberProcedureId === memberId,
    );
    const renderablePDetails = pDetails.filter(hasRenderableProcedureDetail);
    const memberProcedureMeta = getMemberProcedureMeta(memberId);
    const commonProcedureNotes = getCommonProcedureNotes(memberId);

    const memberBodyParts = [
      ...memberProcedures
        .filter((mp: any) => !memberId || mp.memberProcedureId === memberId)
        .map((mp: any) =>
          [mp.bodyPartLabel, mp.bodyPartName].filter(Boolean).join(" "),
        ),
      ...visitBodyParts
        .filter((bp: any) => !memberId || bp.memberProcedureId === memberId)
        .map((bp: any) =>
          [bp.bodyPartLabel, bp.bodyPartName].filter(Boolean).join(" "),
        ),
    ]
      .filter(Boolean)
      .reduce((acc: string[], item: string) => {
        if (!acc.includes(item)) acc.push(item);
        return acc;
      }, []);

    if (renderablePDetails.length === 0) {
      return null;
    }

    return (
      <div key={memberId ?? memberIndex} style={boxStyle}>
        <div
          style={{
            fontWeight: 700,
            marginBottom: "6px",
            color: "#1f2937",
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}
        >
          {memberBodyParts.length > 0 ? (
            <span
              style={{ display: "inline-flex", flexWrap: "wrap", gap: "4px" }}
            >
              {/* <span style={{ fontWeight: 700 }}>Tooth:</span> */}
              <img
                src={TeethIcon}
                alt="tooth"
                style={{
                  width: "12px",
                  height: "12px",
                  verticalAlign: "middle",
                }}
              />
              <span style={{ fontWeight: 700 }}>
                {memberBodyParts.join(", ")}
              </span>
            </span>
          ) : memberProcedureMeta.isGeneral &&
            memberProcedureMeta.generalProcedureName ? (
            <span style={{ fontWeight: 700 }}>
              {memberProcedureMeta.generalProcedureName}
            </span>
          ) : null}
        </div>

        {commonProcedureNotes.length > 0 && (
          <div
            style={{
              color: "#555",
              marginBottom: renderablePDetails.length > 0 ? "8px" : 0,
              lineHeight: "1.5",
            }}
          >
            {commonProcedureNotes.map((note: string, noteIndex: number) => (
              <div key={`${memberId ?? memberIndex}-common-note-${noteIndex}`}>
                {note}
              </div>
            ))}
          </div>
        )}

        {renderablePDetails.map((detail: any, idx: number) => {
          const detailVisits = visits
            .filter(
              (v: any) =>
                v.memberProcedureProcedureDetailId ===
                detail.memberProcedureProcedureDetailId,
            )
            .sort(
              (a: any, b: any) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0),
            );

          const detailVisitParts = getUniqueBodyPartRows(
            detailVisits.flatMap((visit: any) =>
              visitBodyParts.filter(
                (bp: any) =>
                  bp.memberProcedureProcedureDetailVisitId ===
                  visit.memberProcedureProcedureDetailVisitId,
              ),
            ),
          );
          const detailParts =
            detailVisitParts.length > 0
              ? detailVisitParts
              : memberProcedures
                  .filter(
                    (mp: any) =>
                      mp.memberProcedureId === detail.memberProcedureId,
                  )
                  .filter(Boolean);
          const detailProcedureMeta = getMemberProcedureMeta(
            detail.memberProcedureId,
          );
          const detailStatus =
            (detail.processStatus || "").toLowerCase() === "completed"
              ? "Completed"
              : "Pending";

          const completedVisits = detailVisits.filter((visit: any) => {
            const visitStatus =
              (visit.processStatus || "").toLowerCase() === "completed"
                ? "Completed"
                : "Pending";
            return visitStatus === "Completed";
          });

          const shouldShowFallbackDetail = detailStatus === "Completed";

          if (!shouldShowFallbackDetail && completedVisits.length === 0)
            return null;

          return (
            <div
              key={detail.memberProcedureProcedureDetailId || idx}
              style={{ marginTop: idx === 0 ? 0 : "8px" }}
            >
              <div
                style={{
                  fontWeight: 700,
                  marginBottom: "6px",
                  color: "#111",
                }}
              >
                <span
                  style={{
                    display: "inline-flex",
                    width: "16px",
                    height: "16px",
                    borderRadius: "5px",
                    background: "#f0f2f5",
                    color: "#555",
                    fontSize: "10px",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px solid #e2e4e8",
                    marginRight: "6px",
                  }}
                >
                  {idx + 1}
                </span>
                {detail.procedureName}
              </div>

              {completedVisits.length > 0
                ? completedVisits.map((visit: any, vi: number) => {
                    const vParts = visitBodyParts.filter(
                      (bp: any) =>
                        bp.memberProcedureProcedureDetailVisitId ===
                        visit.memberProcedureProcedureDetailVisitId,
                    );
                    const partsForVisit =
                      vParts.length > 0 ? vParts : detailParts;
                    const partsText = renderBodyParts(partsForVisit);
                    const generalProcedureLabel =
                      !partsText &&
                      detailProcedureMeta.isGeneral &&
                      detailProcedureMeta.generalProcedureName
                        ? detailProcedureMeta.generalProcedureName
                        : "";
                    const visitNote = stripHtml(
                      visit.description ||
                        visit.note ||
                        detail.description ||
                        detail.note,
                    );
                    const line = lineJoin([
                      detail.procedureTypeCode === "MULTIPLE_VISIT"
                        ? visit.procedureName || detail.procedureName
                        : undefined,
                    ]);

                    return (
                      <div
                        key={visit.memberProcedureProcedureDetailVisitId || vi}
                        style={{
                          padding: "4px 0",
                          borderTop:
                            vi === 0 ? "1px dashed #e5e7eb" : undefined,
                          color: "#444",
                          display: "flex",
                          flexDirection: "column",
                          gap: "2px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            flexWrap: "wrap",
                          }}
                        >
                          {partsText && (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <img
                                src={TeethIcon}
                                alt="tooth"
                                style={{
                                  width: "12px",
                                  height: "12px",
                                  objectFit: "contain",
                                }}
                              />
                              <span>{partsText}</span>
                            </span>
                          )}
                          {!partsText && generalProcedureLabel && (
                            <span style={{ fontWeight: 700 }}>
                              {generalProcedureLabel}
                            </span>
                          )}
                          <span style={{ fontWeight: "bold" }}>{line}</span>
                        </div>
                        {visitNote && (
                          <div
                            style={{
                              marginTop: "2px",
                              color: "#555",
                              paddingLeft: partsText ? "20px" : 0,
                            }}
                          >
                            {visitNote}
                          </div>
                        )}
                      </div>
                    );
                  })
                : (() => {
                    if (detailStatus !== "Completed") return null;
                    const partsText = renderBodyParts(detailParts);
                    const generalProcedureLabel =
                      !partsText &&
                      detailProcedureMeta.isGeneral &&
                      detailProcedureMeta.generalProcedureName
                        ? detailProcedureMeta.generalProcedureName
                        : "";
                    const line = lineJoin([
                      detail.procedureTypeCode === "MULTIPLE_VISIT"
                        ? detail.procedureName
                        : undefined,
                    ]);
                    return (
                      <div
                        style={{
                          padding: "4px 0",
                          color: "#444",
                          display: "flex",
                          flexDirection: "column",
                          gap: "2px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            flexWrap: "wrap",
                          }}
                        >
                          {partsText && (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <img
                                src={TeethIcon}
                                alt="tooth"
                                style={{
                                  width: "12px",
                                  height: "12px",
                                  objectFit: "contain",
                                }}
                              />
                              <span>{partsText}</span>
                            </span>
                          )}
                          {!partsText && generalProcedureLabel && (
                            <span style={{ fontWeight: 700 }}>
                              {generalProcedureLabel}
                            </span>
                          )}
                          <span style={{ fontWeight: "bold" }}>{line}</span>
                        </div>
                        {stripHtml(detail.description || detail.note) && (
                          <div
                            style={{
                              marginTop: "2px",
                              color: "#555",
                              paddingLeft: partsText ? "20px" : 0,
                            }}
                          >
                            {stripHtml(detail.description || detail.note)}
                          </div>
                        )}
                      </div>
                    );
                  })()}
            </div>
          );
        })}
      </div>
    );
  };

  const renderedMemberBlocks =
    memberIds.length > 0
      ? memberIds.map((mid, idx) => renderMemberBlock(mid, idx)).filter(Boolean)
      : [renderMemberBlock(null, 0)].filter(Boolean);

  if (renderedMemberBlocks.length === 0) {
    return null;
  }

  return (
    <div style={{ ...styles.container, display: "block" }}>
      <div
        style={{
          ...styles.cellContainer,
          minWidth: "100%",
          maxWidth: "100%",
          marginBottom: "6px",
        }}
      >
        <div style={{ ...styles.cell, fontSize: "10px" }}>PROCEDURE</div>
      </div>

      <div
        style={{
          fontSize: "11px",
          textAlign: "left",
          lineHeight: "1.5",
          wordBreak: "break-word",
          padding: "6px 5px",
          display: "grid",
          gap: "8px",
        }}
      >
        {renderedMemberBlocks}
      </div>
    </div>
  );
};

// Diagnosis Component
const Diagnosis = ({ opdContent }: { opdContent: any }) => {
  const diagnosisList =
    opdContent?.diagnoses
      ?.map((item: any) => item?.diagnosisSummary || "")
      .join(",")
      .split(",")
      .map((item: string) => item.trim())
      .filter(Boolean) || [];

  return (
    <div style={styles.container}>
      <div style={styles.cellContainer}>
        <div style={{ ...styles.cell, fontSize: "10px" }}>DIAGNOSIS</div>
      </div>
      <div style={{ fontSize: "11px" }}>
        {diagnosisList.length > 0 && (
          <div
            style={{
              padding: "5px 5px 0 5px",
              display: "flex",
              flexWrap: "wrap",
              gap: "5px",
            }}
          >
            {diagnosisList.map((diagnosis: string, index: number) => (
              <div key={index} style={styles.followupContainer}>
                {diagnosis.trim()}
              </div>
            ))}
          </div>
        )}
        {opdContent?.diagnosisNotes[0]?.isNoteEnabled && (
          <div style={{ padding: "5px 5px 5px 5px" }} className="content">
            <div>
              {opdContent?.diagnosisNotes?.map((item: any, index: number) => (
                <div key={index}>
                  {parse(
                    item?.note?.replace(/^<p>/, "")?.replace(/<\/p>$/, ""),
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// FollowUp Component
const FollowUp = ({ opdContent }: { opdContent: any }) => {
  const hasFollowUpContent =
    opdContent?.followUps?.length > 0 &&
    (opdContent?.followUps[0]?.followUpDuration > 0 ||
      opdContent?.followUps[0]?.followUpReason ||
      opdContent?.followUps.some(
        (fi: any) =>
          fi.followUpInstructionData &&
          fi.followUpInstructionData.trim() !== "",
      ));

  if (!hasFollowUpContent) return null;

  return (
    <div
      style={{ borderTop: hasFollowUpContent ? "1px solid #aaa9a9" : "none" }}
    >
      <div style={{ display: "flex" }}>
        <div style={styles.cellContainer}>
          <div style={{ ...styles.cell, fontSize: "10px" }}>FOLLOW-UP</div>
        </div>
        <div style={{ fontWeight: 600, padding: "5px" }}>
          {parseFloat(opdContent?.followUps[0]?.followUpDuration || "0") > 0
            ? opdContent?.followUps[0]?.languageCode === "ENGLISH-IN"
              ? `${opdContent?.followUps[0]?.followUpAfterText} ${opdContent?.followUps[0]?.followUpDuration} ${opdContent?.followUps[0]?.followUpDurationUnit} (${opdContent?.followUps[0]?.followUpDay} ${opdContent?.followUps[0]?.formatedFollowUpDate})`
              : `${opdContent?.followUps[0]?.followUpDuration} ${opdContent?.followUps[0]?.followUpDurationUnit} ${opdContent?.followUps[0]?.followUpAfterText} (${opdContent?.followUps[0]?.followUpDay} ${opdContent?.followUps[0]?.formatedFollowUpDate})`
            : ""}
          {opdContent?.followUps[0]?.followUpReason
            ? ` - ${opdContent.followUps[0].followUpReason}`
            : ""}
          {(parseFloat(opdContent?.followUps[0]?.followUpDuration || "0") > 0 ||
            opdContent?.followUps[0]?.followUpReason) &&
          opdContent?.followUps[0]?.locationName
            ? ` - ${opdContent.followUps[0].locationName}
                ${
                  opdContent?.followUps[0]?.branchName
                    ? `(${opdContent.followUps[0].branchName})`
                    : ""
                }`
            : ""}
        </div>
      </div>
      <div style={{ paddingTop: "2px", fontSize: "13px" }}>
        <div style={{ paddingLeft: "16px" }}>
          {opdContent?.followUps?.length > 0 && (
            <ul>
              {opdContent?.followUps
                ?.filter(
                  (x: any) =>
                    x.followUpInstructionData !== "" &&
                    x.followUpInstructionData !== null,
                )
                ?.map((fi: any, index: number) => (
                  <li key={index}>
                    {parse(
                      fi?.followUpInstructionData
                        ?.replace(/^<p>/, "")
                        ?.replace(/<\/p>$/, ""),
                    )}
                  </li>
                ))}
            </ul>
          )}
        </div>
        <div>
          {parse(
            opdContent?.followUps[0]?.followUpNote
              ?.replace(/^<p>/, "")
              ?.replace(/<\/p>$/, ""),
          )}
        </div>
      </div>
    </div>
  );
};

// PRESCRIPTION
const Prescription = ({ opdContent }: { opdContent: any }) => {
  return (
    <div
      style={{
        marginTop: "5px",
      }}
    >
      {opdContent?.prescriptionDetailsHtml?.length > 0 && (
        <div
          style={{
            border: "1px solid #aaa9a9",
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          <table style={styles.table}>
            <thead>
              <tr>
                <th
                  style={{
                    ...styles.th,
                    borderLeft: "none",
                    width: "4%",
                    fontSize: "13px",
                  }}
                >
                  <span
                    style={{
                      position: "relative",
                      display: "inline-block",
                      width: "14px",
                      height: "13px",
                      fontWeight: "bold",
                    }}
                  >
                    <span
                      style={{
                        position: "absolute",
                        fontSize: "16px",
                        left: -1,
                        lineHeight: 1,
                      }}
                    >
                      R
                    </span>

                    <span
                      style={{
                        position: "absolute",
                        fontSize: "14px",
                        left: "5px",
                        top: "5px",
                        lineHeight: 1,
                      }}
                    >
                      x
                    </span>
                  </span>
                </th>
                <th style={{ ...styles.th, width: "30%" }}>Medicine</th>
                <th style={{ ...styles.th, width: "40%" }}>
                  Schedule, Dose and Instructions
                </th>
                <th style={{ ...styles.th, width: "14%" }}>Duration</th>
                <th style={{ ...styles.th, borderRight: "none", width: "12%" }}>
                  Qty
                </th>
              </tr>
            </thead>
            <tbody>
              {opdContent?.prescriptionDetailsHtml?.map((item, index, arr) => {
                const length = arr?.length;
                const detailSteps =
                  opdContent?.prescriptionDetailStepsHtml?.filter(
                    (x) =>
                      x?.opdPrescriptionDetailId ===
                      item?.opdPrescriptionDetailId,
                  ) || [];

                const lastDoseIndex = detailSteps.reduce(
                  (lastIdx, step, idx) => {
                    const hasDose =
                      (step?.morningDoseCalcValue ?? 0) > 0 ||
                      (step?.afternoonDoseCalcValue ?? 0) > 0 ||
                      (step?.eveningDoseCalcValue ?? 0) > 0 ||
                      (step?.nightDoseCalcValue ?? 0) > 0;
                    return hasDose ? idx : lastIdx;
                  },
                  -1,
                );
                return (
                  <React.Fragment key={index}>
                    <tr style={styles.row}>
                      <td
                        style={{
                          ...styles.td,
                          borderLeft: "none",
                          fontWeight: 600,
                          borderBottom:
                            length === index + 1 ? "none" : "1px solid #aaa9a9",
                        }}
                      >
                        {index + 1}
                      </td>

                      <td
                        style={{
                          ...styles.tdLeftAlign,
                          padding: "0px 5px 0px 5px",
                          borderBottom:
                            length === index + 1 ? "none" : "1px solid #aaa9a9",
                        }}
                      >
                        <span style={{ fontWeight: "bold" }}>
                          {item?.medicineIdLabel || ""}
                        </span>
                      </td>
                      <td
                        colSpan={3}
                        style={{
                          ...styles.td,
                          borderBottom:
                            length === index + 1 ? "none" : "1px solid #aaa9a9",
                          borderRight: "none",
                          verticalAlign: "top",
                        }}
                      >
                        <table
                          style={{
                            width: "100%",
                            minHeight: "100%",
                            borderCollapse: "collapse",
                          }}
                        >
                          <tbody style={{ minHeight: "100%", padding: "0px" }}>
                            <tr style={{ minHeight: "100%", padding: "0px" }}>
                              {/* Schedule + Duration (81.8%) */}
                              <td
                                style={{
                                  width: "81.8%",
                                  padding: 0,
                                  minHeight: "100%",
                                }}
                              >
                                <table
                                  style={{
                                    width: "100%",
                                    minHeight: "100%",
                                    borderCollapse: "collapse",
                                  }}
                                >
                                  {opdContent?.prescriptionDetailStepsHtml
                                    ?.filter(
                                      (x) =>
                                        x?.opdPrescriptionDetailId ===
                                        item?.opdPrescriptionDetailId,
                                    )
                                    ?.map(
                                      (
                                        detailStep: any,
                                        stepIndex: number,
                                        arr: any[],
                                      ) => (
                                        <React.Fragment
                                          key={
                                            detailStep?.opdPrescriptionDetailStepId
                                          }
                                        >
                                          {/* Dose row */}
                                          {((["SOS", "STAT"].includes(
                                            detailStep?.doseFrequencyCode,
                                          ) &&
                                            detailStep?.sosDoseCalcValue >=
                                              0) ||
                                            detailStep?.morningDoseCalcValue >
                                              0 ||
                                            detailStep?.afternoonDoseCalcValue >
                                              0 ||
                                            detailStep?.eveningDoseCalcValue >
                                              0 ||
                                            detailStep?.nightDoseCalcValue >
                                              0 ||
                                            detailStep?.doseCalcValue > 0) && (
                                            <tr
                                              style={{
                                                borderBottom:
                                                  !["SOS", "STAT"].includes(
                                                    detailStep?.doseFrequencyCode,
                                                  ) &&
                                                  detailStep?.doseCalcValue <=
                                                    0 &&
                                                  (item?.routeOfAdministrationName ||
                                                    // detailStep?.doseFrequencyLabel ||
                                                    item?.relationToFoodTimeGapName)
                                                    ? "1px solid #aaa9a9"
                                                    : "none",
                                                borderTop:
                                                  stepIndex >= 1
                                                    ? "1px solid #aaa9a9"
                                                    : "none",
                                              }}
                                            >
                                              {/* Schedule/Instructions */}
                                              <td
                                                style={{
                                                  width: "74.15%",
                                                  padding: "5px",
                                                  verticalAlign: "top",
                                                }}
                                              >
                                                {["SOS", "STAT"].includes(
                                                  detailStep?.doseFrequencyCode,
                                                ) ? (
                                                  <div
                                                    style={{
                                                      fontWeight: 600,
                                                      wordBreak: "break-word",
                                                      textAlign: "left",
                                                    }}
                                                  >
                                                    <span
                                                      style={{
                                                        fontSize: "16px",
                                                      }}
                                                    >
                                                      {detailStep?.sosDoseCalcValue !==
                                                      0
                                                        ? `${detailStep?.sosDoseDisplayValue}`
                                                        : ""}{" "}
                                                    </span>
                                                    {/* {item?.printUnitLabel}{" "} */}
                                                    {detailStep?.sosDoseCalcValue !==
                                                    0
                                                      ? item?.printUnitLabel
                                                      : ""}{" "}
                                                    {detailStep?.doseFrequencyLabel ||
                                                      ""}
                                                    {detailStep?.indicationOfSosMedicineLabel
                                                      ? ` ${detailStep?.indicationOfSosMedicineLabel}`
                                                      : ""}{" "}
                                                    {
                                                      item?.routeOfAdministrationName
                                                    }
                                                    {", "}
                                                    {
                                                      item?.relationToFoodTimeGapName
                                                    }
                                                  </div>
                                                ) : (detailStep?.morningDoseCalcValue ??
                                                    0) > 0 ||
                                                  (detailStep?.afternoonDoseCalcValue ??
                                                    0) > 0 ||
                                                  (detailStep?.eveningDoseCalcValue ??
                                                    0) > 0 ||
                                                  (detailStep?.nightDoseCalcValue ??
                                                    0) > 0 ? (
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      justifyContent: "center",
                                                      gap: "10px",
                                                      fontWeight: 600,
                                                      flexWrap: "wrap",
                                                      wordBreak: "break-word",
                                                    }}
                                                  >
                                                    {detailStep?.morningDoseCalcValue >
                                                      0 && (
                                                      <div>
                                                        <div>
                                                          {
                                                            detailStep?.morningDoseDisplayUnitCode
                                                          }
                                                        </div>
                                                        <div>
                                                          {" "}
                                                          {detailStep.morningScheduleTime && (
                                                            <span>
                                                              {dayjs(
                                                                detailStep.morningScheduleTime,
                                                              ).format(
                                                                "hh:mm A",
                                                              )}
                                                            </span>
                                                          )}
                                                        </div>
                                                        <div>
                                                          {/* {detailStep?.morningDoseDisplayValue} {item?.printUnitLabel} */}
                                                          <span
                                                            style={{
                                                              fontSize: "16px",
                                                            }}
                                                          >
                                                            {
                                                              detailStep?.morningDoseDisplayValue
                                                            }
                                                          </span>{" "}
                                                          {item?.printUnitLabel}
                                                        </div>
                                                      </div>
                                                    )}
                                                    {detailStep?.afternoonDoseCalcValue >
                                                      0 && (
                                                      <div>
                                                        <div>
                                                          {
                                                            detailStep?.afternoonDoseDisplayUnitCode
                                                          }
                                                        </div>
                                                        <div>
                                                          {detailStep.afternoonScheduleTime && (
                                                            <span>
                                                              {dayjs(
                                                                detailStep.afternoonScheduleTime,
                                                              ).format(
                                                                "hh:mm A",
                                                              )}
                                                            </span>
                                                          )}
                                                        </div>
                                                        <div>
                                                          {/* {detailStep?.afternoonDoseDisplayValue} {item?.printUnitLabel} */}
                                                          <span
                                                            style={{
                                                              fontSize: "16px",
                                                            }}
                                                          >
                                                            {
                                                              detailStep?.afternoonDoseDisplayValue
                                                            }
                                                          </span>{" "}
                                                          {item?.printUnitLabel}
                                                        </div>
                                                      </div>
                                                    )}
                                                    {detailStep?.eveningDoseCalcValue >
                                                      0 && (
                                                      <div>
                                                        <div>
                                                          {
                                                            detailStep?.eveningDoseDisplayUnitCode
                                                          }
                                                        </div>
                                                        <div>
                                                          {detailStep.eveningScheduleTime && (
                                                            <span>
                                                              {dayjs(
                                                                detailStep.eveningScheduleTime,
                                                              ).format(
                                                                "hh:mm A",
                                                              )}
                                                            </span>
                                                          )}
                                                        </div>
                                                        <div>
                                                          {/* {detailStep?.eveningDoseDisplayValue} {item?.printUnitLabel} */}
                                                          <span
                                                            style={{
                                                              fontSize: "16px",
                                                            }}
                                                          >
                                                            {
                                                              detailStep?.eveningDoseDisplayValue
                                                            }
                                                          </span>{" "}
                                                          {item?.printUnitLabel}
                                                        </div>
                                                      </div>
                                                    )}
                                                    {detailStep?.nightDoseCalcValue >
                                                      0 && (
                                                      <div>
                                                        <div>
                                                          {
                                                            detailStep?.nightDoseDisplayUnitCode
                                                          }
                                                        </div>
                                                        <div>
                                                          {detailStep.nightScheduleTime && (
                                                            <span>
                                                              {dayjs(
                                                                detailStep.nightScheduleTime,
                                                              ).format(
                                                                "hh:mm A",
                                                              )}
                                                            </span>
                                                          )}
                                                        </div>
                                                        <div>
                                                          {/* {detailStep?.nightDoseDisplayValue} {item?.printUnitLabel} */}
                                                          <span
                                                            style={{
                                                              fontSize: "16px",
                                                            }}
                                                          >
                                                            {
                                                              detailStep?.nightDoseDisplayValue
                                                            }
                                                          </span>{" "}
                                                          {item?.printUnitLabel}
                                                        </div>
                                                      </div>
                                                    )}
                                                  </div>
                                                ) : (
                                                  <div
                                                    style={{
                                                      // display: "flex",
                                                      // justifyContent: "left",
                                                      // gap: "10px",
                                                      textAlign: "left",
                                                      fontWeight: 600,
                                                      flexWrap: "wrap",
                                                      wordBreak: "break-word",
                                                    }}
                                                  >
                                                    <div>
                                                      {
                                                        detailStep?.doseDisplayValue
                                                      }{" "}
                                                      {item?.printUnitLabel}{" "}
                                                      {
                                                        detailStep?.doseFrequencyLabel
                                                      }{" "}
                                                      {
                                                        item?.routeOfAdministrationName
                                                      }
                                                      {", "}
                                                      {
                                                        item?.relationToFoodTimeGapName
                                                      }
                                                    </div>
                                                  </div>
                                                )}
                                              </td>

                                              {/* Duration */}
                                              <td
                                                style={{
                                                  width: "25.85%",
                                                  borderLeft:
                                                    detailStep?.durationValue >
                                                    0
                                                      ? "1px solid #aaa"
                                                      : "none",
                                                  textAlign: "center",
                                                  fontWeight: 600,
                                                }}
                                              >
                                                {detailStep?.durationValue >
                                                  0 &&
                                                  `${detailStep?.durationValue} ${detailStep?.durationUnitLabel}`}
                                              </td>
                                            </tr>
                                          )}

                                          {/* STAT/Extra row if needed */}
                                          {/* {!["SOS", "STAT"].includes(
                                            detailStep?.doseFrequencyCode
                                          ) &&
                                            detailStep?.doseCalcValue <= 0 &&
                                            (item?.routeOfAdministrationName ||
                                              detailStep?.doseFrequencyLabel ||
                                              item?.relationToFoodTimeGapName) && (
                                              <tr>
                                                <td
                                                  colSpan={2}
                                                  style={{ padding: "5px" }}
                                                >
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      flexWrap: "wrap",
                                                    }}
                                                  >
                                                    {
                                                      item?.routeOfAdministrationName
                                                    }{"  "}
                                                    {item?.relationToFoodTimeGapValue >
                                                      0
                                                      ? `${item?.relationToFoodTimeGapValue} ${item?.relationToFoodTimeGapUnitLabel}`
                                                      : ""}{" "}
                                                    {
                                                      item?.relationToFoodTimeGapName
                                                    } 
                                                  </div>
                                                </td>
                                              </tr>
                                            )} */}
                                          {!["SOS", "STAT"].includes(
                                            detailStep?.doseFrequencyCode,
                                          ) &&
                                            detailStep?.doseCalcValue <= 0 &&
                                            (stepIndex === lastDoseIndex ||
                                              lastDoseIndex === -1) &&
                                            (item?.routeOfAdministrationName ||
                                              detailStep?.doseFrequencyLabel ||
                                              item?.relationToFoodTimeGapName) && (
                                              <tr>
                                                <td
                                                  colSpan={2}
                                                  style={{
                                                    padding:
                                                      item?.routeOfAdministrationName ||
                                                      item?.relationToFoodTimeGapName
                                                        ? "5px"
                                                        : "0px",
                                                  }}
                                                >
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      flexWrap: "wrap",
                                                    }}
                                                  >
                                                    {
                                                      item?.routeOfAdministrationName
                                                    }
                                                    {"  "}
                                                    {item?.relationToFoodTimeGapValue >
                                                    0
                                                      ? `${item?.relationToFoodTimeGapValue} ${item?.relationToFoodTimeGapUnitLabel}`
                                                      : ""}{" "}
                                                    {
                                                      item?.relationToFoodTimeGapName
                                                    }
                                                    {/* {" "}
                                                    {
                                                      detailStep?.doseFrequencyLabel
                                                    } */}
                                                  </div>
                                                </td>
                                              </tr>
                                            )}
                                        </React.Fragment>
                                      ),
                                    )}
                                </table>
                              </td>

                              {/* Qty (18.2%) */}
                              <td
                                style={{
                                  width: "18.2%",
                                  borderLeft: "1px solid #aaa9a9",
                                }}
                              >
                                {item?.quantity > 0 &&
                                  `${item?.quantity} ${item?.quantityUnitLabel}`}
                              </td>
                            </tr>

                            {/* Medicine Instructions */}
                            {opdContent?.prescriptionDetailMedicineInstructionsHtml
                              ?.filter(
                                (x) =>
                                  x?.opdPrescriptionDetailId ===
                                  item?.opdPrescriptionDetailId,
                              )
                              ?.map((stepIns: any, stepIndex: number) =>
                                stepIns?.opdPrescriptionDetailMedicineInstructionData ? (
                                  <tr
                                    key={stepIndex}
                                    style={{ borderTop: "1px solid #aaa9a9" }}
                                  >
                                    <td colSpan={3} style={{ padding: "5px" }}>
                                      <div
                                        style={{
                                          textAlign: "left",
                                          wordBreak: "break-word",
                                        }}
                                      >
                                        {parse(
                                          stepIns?.opdPrescriptionDetailMedicineInstructionData
                                            ?.replace(/^<p>/, "")
                                            ?.replace(/<\/p>$/, "") || "",
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                ) : null,
                              )}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Prescription Note */}
      {opdContent?.prescriptionReportsHtml?.map((presc) => (
        <div
          style={{ fontSize: "12px", marginTop: "8px" }}
          key={presc?.opdPrescriptionId}
        >
          {parse(
            presc?.prescriptionNote?.replace(/^<p>/, "")?.replace(/<\/p>$/, ""),
          )}
        </div>
      ))}

      {/* General Instruction */}
      {opdContent?.prescriptionInstructionsHtml?.length > 0 && (
        <div style={{ fontSize: "12px", marginTop: "3px", marginLeft: "16px" }}>
          <ul>
            {opdContent?.prescriptionInstructionsHtml?.map((presc, index) => (
              <li key={index}>
                {parse(
                  presc?.opdPrescriptionInstructionData
                    ?.replace(/^<p>/, "")
                    ?.replace(/<\/p>$/, ""),
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* General Instruction Note */}
      {opdContent?.prescriptionReportsHtml?.map((presc) => (
        <div style={{ fontSize: "10px" }} key={presc?.opdPrescriptionId}>
          {parse(
            presc?.instructionNote?.replace(/^<p>/, "")?.replace(/<\/p>$/, ""),
          )}
        </div>
      ))}
    </div>
  );
};

// Doctor to Be referred
const DoctorToBeReferred = ({ opdContent }: { opdContent: any }) => {
  const doctors = opdContent?.doctorToBeReferred;

  if (!doctors || doctors.length === 0) return null;

  return (
    <div
      style={{
        display: "flex",
        borderTop: "1px solid #c7c5c5",
        marginTop: "4px",
      }}
    >
      <div style={styles.cellContainer}>
        <div style={{ ...styles.cell, fontSize: "10px" }}>
          DOCTORS TO BE REFERRED
        </div>
      </div>
      <div
        style={{
          flex: 1,
          padding: "4px 0 4px 6px",
        }}
      >
        {doctors.map((doc: any, index: number) => (
          <div
            key={index}
            style={{
              marginBottom: "4px",
            }}
          >
            <div>
              <strong>
                {index + 1}. {doc?.doctorToBeReferredName}
              </strong>
              {doc?.qualification ? ` (${doc.qualification})` : ""}
              <div
                style={{ padding: "4px", fontSize: "11px" }}
                className="content"
              >
                {doc?.hospitalName && <div>{doc.hospitalName}</div>}

                {(doc?.address1 ||
                  doc?.address2 ||
                  doc?.city ||
                  doc?.state ||
                  doc?.country) && (
                  <div>
                    {[
                      doc?.address1,
                      doc?.address2,
                      doc?.city,
                      doc?.state,
                      doc?.country,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </div>
                )}

                <div>
                  {doc?.mobileNumber && (
                    <>
                      <span style={{ fontWeight: "bold" }}>M: </span>
                      {doc.mobileNumber}
                    </>
                  )}

                  {doc?.alternativeAppointmentNumber && (
                    <>
                      {doc?.mobileNumber ? ", " : ""}
                      {doc.alternativeAppointmentNumber}
                    </>
                  )}

                  {doc?.email && (
                    <>
                      {(doc?.mobileNumber ||
                        doc?.alternativeAppointmentNumber) &&
                        " "}
                      <span style={{ fontWeight: "bold" }}>E: </span>
                      {doc.email}
                    </>
                  )}
                </div>

                {doc?.clinicalNote && (
                  <div>
                    <span style={{ fontWeight: "bold" }}>Clinical Note:</span>{" "}
                    {parse(
                      doc.clinicalNote
                        .replace(/^<p>/, "")
                        .replace(/<\/p>$/, ""),
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Main Component
const PriscriptionContent = ({
  opdContent,
  selectedSections,
}: {
  opdContent: any;
  selectedSections: { [key: string]: boolean };
}) => {
  const { userContext } = React.useContext(PreferencesContext);
  // const showDental = React.useMemo(() => {
  //   return userContext.allowedSystemColl?.some(
  //     (system) => system.examinationSystemCode === "DENTAL",
  //   );
  // }, [userContext.allowedSystemColl]);

  const showDental = React.useMemo(() => {
    return userContext.allowedSystemColl?.some(
      (system) =>
        system.specialityId === userContext.specialityId &&
        system.examinationSystemCode === "DENTAL",
    );
  }, [userContext.allowedSystemColl, userContext.specialityId]);
  const isSuperAdmin =
    (userContext?.loginDetail?.userRole || "").toString().toLowerCase() ===
    "superadmin";
  const shouldShowChiefComplaints = hasChiefComplaintContent(opdContent);
  const shouldShowInvestigation = hasInvestigationContent(opdContent);

  return (
    <div style={{ fontSize: "12px" }}>
      {selectedSections?.["SPECIAL_MEDICAL_CONDITION"] &&
        opdContent?.medicalCondition?.length > 0 && (
          <SpecialMedicalCondition opdContent={opdContent} />
        )}

      {selectedSections?.["VITALS_AND_ANTHROPOMETRY"] &&
        opdContent?.examinationVitalParameters &&
        (opdContent?.examinationVitalParameters?.weight > 0 ||
          opdContent?.examinationVitalParameters?.temperatureAmount > 0 ||
          opdContent?.examinationVitalParameters?.height > 0 ||
          opdContent?.examinationVitalParameters?.pulseRate > 0 ||
          opdContent?.examinationVitalParameters?.spo2Amount > 0 ||
          opdContent?.examinationVitalParameters?.bmi > 0 ||
          (opdContent?.examinationVitalParameters?.bpDisplayValue !== "/" &&
            opdContent?.examinationVitalParameters?.bpDisplayValue !== "") ||
          opdContent?.examinationVitalParameters?.bsa > 0 ||
          opdContent?.examinationVitalParameters?.respiratoryRate > 0 ||
          opdContent?.examinationVitalParameters?.rbs > 0 ||
          opdContent?.examinationVitalParameters?.motherHeight > 0 ||
          opdContent?.examinationVitalParameters?.fatherHeight > 0) && (
          <VitalParameter opdContent={opdContent} />
        )}

      {selectedSections?.["CHIEF_COMPLAINTS"] && shouldShowChiefComplaints && (
        <ChiefComplaint opdContent={opdContent} />
      )}

      {/* {selectedSections?.["HISTORY"] &&
        ((opdContent?.patientHistory?.length > 0) || (opdContent?.patientHistoryPastHistory?.length > 0)) && (
          <History opdContent={opdContent} />
        )} */}

      {selectedSections?.["HISTORY"] && (showDental || isSuperAdmin) && (
        <DentalHistory opdContent={opdContent} />
      )}

      {selectedSections?.["EXAMINATION"] && (showDental || isSuperAdmin) && (
        <DentalExamination opdContent={opdContent} />
      )}

      {selectedSections?.["HISTORY"] && (!showDental || isSuperAdmin) && (
        <History opdContent={opdContent} />
      )}

      {selectedSections?.["EXAMINATION"] && (!showDental || isSuperAdmin) && (
        <Examination opdContent={opdContent} />
      )}
      {selectedSections?.["VACCINATION"] &&
        opdContent?.patientVaccinations?.length > 0 && (
          <Vaccination opdContent={opdContent} />
        )}

      {selectedSections?.["INVESTIGATION"] && shouldShowInvestigation && (
        <Investigation opdContent={opdContent} />
      )}

      {selectedSections?.["PROCEDURE"] && (showDental || isSuperAdmin) && (
        <Procedure opdContent={opdContent} />
      )}

      {selectedSections?.["DIAGNOSIS"] &&
        (opdContent?.diagnoses?.length > 0 ||
          opdContent?.diagnosisNotes?.length > 0) &&
        (opdContent?.diagnoses[0]?.diagnosisSummary ||
          opdContent?.diagnosisNotes[0]?.isNoteEnabled) && (
          <Diagnosis opdContent={opdContent} />
        )}

      {selectedSections?.["PRESCRIPTION"] &&
        (opdContent?.prescriptionDetailsHtml?.length > 0 ||
          opdContent?.prescriptionReportsHtml?.length > 0 ||
          opdContent?.prescriptionInstructionsHtml?.length > 0) && (
          <Prescription opdContent={opdContent} />
        )}

      {selectedSections?.["FOLLOWUP"] && opdContent?.followUps?.length > 0 && (
        <FollowUp opdContent={opdContent} />
      )}

      {selectedSections?.["DOCTOR_TO_BE_REFERRED"] &&
        opdContent?.doctorToBeReferred?.length > 0 && (
          <DoctorToBeReferred opdContent={opdContent} />
        )}
    </div>
  );
};

export default PriscriptionContent;
